/**
 * The dashboard shell, on both dashboard pages: /dashboard?formId= (the event,
 * dashboard.html) and /dashboard with no event or /my-projects (All events,
 * my-projects.html). Named v2 for the redesign that replaced the old
 * dashboard on 2026-10-25.
 *
 * All data loading stays with admin.js / my-projects.js and the tab
 * controllers; this file drives the shell: the rail with the host's events,
 * the event header, desktop collapse and the phone sheet.
 */
(function () {
    'use strict';

    const $ = (id) => document.getElementById(id);
    const body = document.body;
    const COLLAPSE_KEY = 'dv2RailCollapsed';
    const SECTION_SUFFIX = /\s+-\s+Responses$/;
    const SEARCH_FROM = 4; // events on the home before a search box is worth it
    const isPhone = () => window.matchMedia('(max-width: 767px)').matches;

    // ---- Language ------------------------------------------------------------
    // The page's <html lang> picks the strings; links keep the language prefix
    // (/he/dashboard…). Relative days and dates come from Intl, so "in 5
    // days" / "tomorrow" / "16 days ago" are grammatical in every language.
    const LANG = ((document.documentElement.lang || 'en').slice(0, 2).toLowerCase());
    const PREFIX = (window.location.pathname.match(/^\/(es|fr|de|pt|he|ar)\//) || [])[1] ? '/' + window.location.pathname.split('/')[1] : '';
    // The landing page's live demo (?demo=1): a signed-out visitor, so the
    // rail keeps them in the demo and drops what only an account can open
    // (dv2-demo in dashboard-v2.css).
    const IS_DEMO = new URLSearchParams(window.location.search).get('demo') === '1';
    if (IS_DEMO) body.classList.add('dv2-demo');
    const STR = {
        en: { guestLink: 'Copy guest link', collapseSidebar: 'Collapse sidebar', expandSidebar: 'Expand sidebar', signOut: 'Sign out', editForm: 'Edit form', past: 'Past', allEvents: 'All events', language: 'Language', on: 'On', setSharing: 'Sharing', setAccess: 'Co-hosts and access', setAccessHint: 'Let someone else manage this event', setNotif: 'Response notifications', setNotifHint: 'Email or push when a guest replies', setOpenForm: 'Open the form', setOpenFormHint: "The real form your guests get. Replies sent there are saved.", setQr: 'Download the QR code', setForm: 'Form', setEdit: 'Edit the form', setEditHint: 'Questions, design and translations', setPreview: 'Preview as a guest', setPreviewHint: "Try it out: nothing you send here is saved.", setPreviewTagsHint: "See what each tagged guest gets. Nothing is saved.", setPlan: 'Plan', setUpgrade: 'Upgrade this event', setUpgradeHint: 'More replies, reply alerts, QR check-in and more', planFree: 'Free plan · shows up to {cap} replies · Event Pro: {price} one-time, up to 150', planWall: '{cap} of {n} replies shown · Event Pro {price} shows all {n}', planPro: 'Event Pro · Signature adds a wedding website and seating for {price} more', planSee: 'See plans', setSiteReg: 'Website and registry', setRegistry: 'Gift registry', setRegistryHint: 'Gifts and contributions', setWebsite: 'Event website', setCopies: 'Copies', setDuplicate: 'Duplicate this event', setDuplicateHint: 'A fresh copy with no replies', setTemplate: 'Save as a template', setBranding: 'Planner branding', setDelete: 'Delete this event', setDeleteHint: 'You can undo it right after', setShared: 'Shared with you: only the owner can duplicate or delete it.', replies_one: '{n} reply', replies_other: '{n} replies', newSince: '+{n} new', sinceLast: 'Since you last opened this dashboard', replied: 'Replied', attending: 'Attending', event_one: 'Event', event_other: 'Events', upcoming: 'Upcoming', repliesTile: 'Replies', seatingDone: 'Seating done', searchEvents: 'Search events', noDate: 'No date set', rsvp: 'RSVP {rel}', rsvpClosed: 'RSVP closed {rel}', noReplies: 'No replies yet.', eventIn: 'The event is {rel}.', rsvpClose: 'RSVPs close {rel}.', remind: "Remind the guests who haven't answered.", remindN: "Remind the guests who haven't answered ({n}).", seatNow: 'RSVPs are closed. Time to seat your guests and set up check-in.', ctaInvite: 'Send invitations', ctaRemind: 'Send a reminder', ctaSeat: 'Open seating', sReplies: 'Replies', sGuests: 'Guests', sAdults: 'Adults', sChildren: 'Children', sMaybe: 'Maybe', sAwaiting: 'Awaiting', sWaitlisted: 'Waitlisted', exportCsv: 'Export CSV', partyOf: 'Party of ', looseHead: 'Not linked to an event', webWord: "Website", webEdit: "Edit website", webView: "View site", webCopy: "Copy link", webCopied: "Link copied", webLive: "Published", webDraft: "Draft", webExpired: "Expired", webDraftNote: "Not published yet. Guests can't see it until you publish it.", webExpiredNote: "This website has expired. Renew it to bring it back online.", webPublish: "Publish", webRenew: "Renew", webSections: "Edit a section", webNames: "Names & date", webDesign: "Design", webHome: "Home", webDetails: "Details", webTravel: "Travel", webFaq: "FAQ", webPhotos: "Photos", webRsvp: "RSVP", webAddress: "Website address", webLangs: "Languages", webPreview: "Preview", webNudgeTitle: "Add a wedding website", webNudgeDesc: "Your story, schedule, travel and FAQ on one page, with this event's RSVP form built in.", webCreate: "Create website", webOwnerOnly: "This event has no website yet. Only its owner can add one.", webLoadError: "Couldn't load the website. Try again in a moment.", moreTools: "More tools" },
        es: { guestLink: 'Copiar enlace', collapseSidebar: 'Contraer la barra lateral', expandSidebar: 'Expandir la barra lateral', signOut: 'Cerrar sesión', editForm: 'Editar formulario', past: 'Pasados', allEvents: 'Todos los eventos', language: 'Idioma', on: 'Activadas', setSharing: 'Compartir', setAccess: 'Coanfitriones y acceso', setAccessHint: 'Deja que otra persona gestione este evento', setNotif: 'Notificaciones de respuestas', setNotifHint: 'Correo o push cuando un invitado responde', setOpenForm: 'Abrir el formulario', setOpenFormHint: "El formulario real que reciben tus invitados. Las respuestas enviadas allí se guardan.", setQr: 'Descargar el código QR', setForm: 'Formulario', setEdit: 'Editar el formulario', setEditHint: 'Preguntas, diseño y traducciones', setPreview: 'Vista previa como invitado', setPreviewHint: "Pruébalo: nada de lo que envíes aquí se guarda.", setPreviewTagsHint: "Mira lo que recibe cada invitado etiquetado. No se guarda nada.", setPlan: 'Plan', setUpgrade: 'Mejorar este evento', setUpgradeHint: 'Más respuestas, avisos de respuesta, check-in con QR y más', planFree: 'Plan gratuito · muestra hasta {cap} respuestas · Event Pro: {price} en un solo pago, hasta 150', planWall: 'Se muestran {cap} de {n} respuestas · Event Pro por {price} muestra las {n}', planPro: 'Event Pro · Signature añade un sitio web de boda y distribución de mesas por {price} más', planSee: 'Ver planes', setSiteReg: 'Sitio web y mesa de regalos', setRegistry: 'Mesa de regalos', setRegistryHint: 'Regalos y aportaciones', setWebsite: 'Sitio web del evento', setCopies: 'Copias', setDuplicate: 'Duplicar este evento', setDuplicateHint: 'Una copia nueva sin respuestas', setTemplate: 'Guardar como plantilla', setBranding: 'Marca del organizador', setDelete: 'Eliminar este evento', setDeleteHint: 'Puedes deshacerlo justo después', setShared: 'Compartido contigo: solo el propietario puede duplicarlo o eliminarlo.', replies_one: '{n} respuesta', replies_other: '{n} respuestas', newSince: '+{n} nuevas', sinceLast: 'Desde la última vez que abriste este panel', replied: 'Respondieron', attending: 'Asistirán', event_one: 'Evento', event_other: 'Eventos', upcoming: 'Próximos', repliesTile: 'Respuestas', seatingDone: 'Mesas listas', searchEvents: 'Buscar eventos', noDate: 'Sin fecha', rsvp: 'Confirmaciones {rel}', rsvpClosed: 'Confirmaciones cerradas {rel}', noReplies: 'Aún no hay respuestas.', eventIn: 'El evento es {rel}.', rsvpClose: 'Las confirmaciones cierran {rel}.', remind: 'Recuerda a los invitados que aún no han respondido.', remindN: 'Recuerda a los invitados que aún no han respondido ({n}).', seatNow: 'Las confirmaciones están cerradas. Es hora de ubicar a tus invitados y preparar el registro de llegada.', ctaInvite: 'Enviar invitaciones', ctaRemind: 'Enviar un recordatorio', ctaSeat: 'Abrir mesas', sReplies: 'Respuestas', sGuests: 'Invitados', sAdults: 'Adultos', sChildren: 'Niños', sMaybe: 'Quizás', sAwaiting: 'Sin respuesta', sWaitlisted: 'En espera', exportCsv: 'Exportar CSV', partyOf: 'Grupo de ', looseHead: 'Sin evento vinculado', webWord: "Sitio web", webEdit: "Editar sitio web", webView: "Ver sitio", webCopy: "Copiar enlace", webCopied: "Enlace copiado", webLive: "Publicado", webDraft: "Borrador", webExpired: "Caducado", webDraftNote: "Aún no está publicado. Tus invitados no pueden verlo hasta que lo publiques.", webExpiredNote: "Este sitio ha caducado. Renuévalo para volver a ponerlo en línea.", webPublish: "Publicar", webRenew: "Renovar", webSections: "Editar una sección", webNames: "Nombres y fecha", webDesign: "Diseño", webHome: "Inicio", webDetails: "Detalles", webTravel: "Viaje", webFaq: "Preguntas frecuentes", webPhotos: "Fotos", webRsvp: "RSVP", webAddress: "Dirección del sitio", webLangs: "Idiomas", webPreview: "Vista previa", webNudgeTitle: "Añade una web de boda", webNudgeDesc: "Vuestra historia, horario, viaje y preguntas frecuentes en un solo lugar, con el formulario de este evento incluido.", webCreate: "Crear sitio web", webOwnerOnly: "Este evento aún no tiene sitio web. Solo su propietario puede añadir uno.", webLoadError: "No se pudo cargar el sitio web. Inténtalo de nuevo en un momento.", moreTools: "Más herramientas" },
        fr: { guestLink: 'Copier le lien', collapseSidebar: 'Réduire la barre latérale', expandSidebar: 'Développer la barre latérale', signOut: 'Se déconnecter', editForm: 'Modifier le formulaire', past: 'Passés', allEvents: 'Tous les événements', language: 'Langue', on: 'Activées', setSharing: 'Partage', setAccess: 'Co-organisateurs et accès', setAccessHint: "Laissez quelqu'un d'autre gérer cet événement", setNotif: 'Notifications de réponses', setNotifHint: 'E-mail ou push quand un invité répond', setOpenForm: 'Ouvrir le formulaire', setOpenFormHint: "Le vrai formulaire que reçoivent vos invités. Les réponses envoyées là sont enregistrées.", setQr: 'Télécharger le QR code', setForm: 'Formulaire', setEdit: 'Modifier le formulaire', setEditHint: 'Questions, design et traductions', setPreview: 'Aperçu côté invité', setPreviewHint: "Essayez-le : rien de ce que vous envoyez ici n'est enregistré.", setPreviewTagsHint: "Voyez ce que reçoit chaque invité étiqueté. Rien n'est enregistré.", setPlan: 'Formule', setUpgrade: 'Passer à la formule supérieure', setUpgradeHint: 'Plus de réponses, des alertes, l’enregistrement par QR code et plus encore', planFree: 'Formule gratuite · affiche jusqu’à {cap} réponses · Event Pro : {price} en paiement unique, jusqu’à 150', planWall: '{cap} réponses affichées sur {n} · Event Pro à {price} les affiche toutes', planPro: 'Event Pro · Signature ajoute un site de mariage et le plan de table pour {price} de plus', planSee: 'Voir les formules', setSiteReg: 'Site et liste de cadeaux', setRegistry: 'Liste de cadeaux', setRegistryHint: 'Cadeaux et participations', setWebsite: "Site de l'événement", setCopies: 'Copies', setDuplicate: 'Dupliquer cet événement', setDuplicateHint: 'Une copie vierge, sans réponses', setTemplate: 'Enregistrer comme modèle', setBranding: "Image de marque de l'organisateur", setDelete: 'Supprimer cet événement', setDeleteHint: 'Vous pourrez annuler juste après', setShared: 'Partagé avec vous : seul le propriétaire peut le dupliquer ou le supprimer.', replies_one: '{n} réponse', replies_other: '{n} réponses', newSince: '+{n} nouvelles', sinceLast: 'Depuis votre dernière visite sur ce tableau de bord', replied: 'Ont répondu', attending: 'Présents', event_one: 'Événement', event_other: 'Événements', upcoming: 'À venir', repliesTile: 'Réponses', seatingDone: 'Plan de table terminé', searchEvents: 'Rechercher des événements', noDate: 'Aucune date', rsvp: 'Réponses {rel}', rsvpClosed: 'Réponses closes {rel}', noReplies: "Aucune réponse pour l'instant.", eventIn: "L'événement a lieu {rel}.", rsvpClose: 'Les réponses se clôturent {rel}.', remind: "Relancez les invités qui n'ont pas encore répondu.", remindN: "Relancez les invités qui n'ont pas encore répondu ({n}).", seatNow: "Les réponses sont closes. Place au plan de table et à l'accueil des invités.", ctaInvite: 'Envoyer les invitations', ctaRemind: 'Envoyer une relance', ctaSeat: 'Ouvrir le plan de table', sReplies: 'Réponses', sGuests: 'Invités', sAdults: 'Adultes', sChildren: 'Enfants', sMaybe: 'Peut-être', sAwaiting: 'Sans réponse', sWaitlisted: "Liste d'attente", exportCsv: 'Exporter en CSV', partyOf: 'Groupe de ', looseHead: 'Non liés à un événement', webWord: "Site web", webEdit: "Modifier le site", webView: "Voir le site", webCopy: "Copier le lien", webCopied: "Lien copié", webLive: "Publié", webDraft: "Brouillon", webExpired: "Expiré", webDraftNote: "Pas encore publié. Vos invités ne le verront qu'une fois publié.", webExpiredNote: "Ce site a expiré. Renouvelez-le pour le remettre en ligne.", webPublish: "Publier", webRenew: "Renouveler", webSections: "Modifier une section", webNames: "Noms et date", webDesign: "Design", webHome: "Accueil", webDetails: "Détails", webTravel: "Voyage", webFaq: "FAQ", webPhotos: "Photos", webRsvp: "RSVP", webAddress: "Adresse du site", webLangs: "Langues", webPreview: "Aperçu", webNudgeTitle: "Ajoutez un site de mariage", webNudgeDesc: "Votre histoire, le programme, le voyage et la FAQ au même endroit, avec le formulaire RSVP de cet événement intégré.", webCreate: "Créer le site", webOwnerOnly: "Cet événement n'a pas encore de site. Seul son propriétaire peut en ajouter un.", webLoadError: "Impossible de charger le site. Réessayez dans un instant.", moreTools: "Plus d'outils" },
        de: { guestLink: 'Link kopieren', collapseSidebar: 'Seitenleiste einklappen', expandSidebar: 'Seitenleiste ausklappen', signOut: 'Abmelden', editForm: 'Formular bearbeiten', past: 'Vergangen', allEvents: 'Alle Events', language: 'Sprache', on: 'An', setSharing: 'Teilen', setAccess: 'Mitgastgeber und Zugriff', setAccessHint: 'Lassen Sie jemand anderen dieses Event verwalten', setNotif: 'Antwort-Benachrichtigungen', setNotifHint: 'E-Mail oder Push, wenn ein Gast antwortet', setOpenForm: 'Formular öffnen', setOpenFormHint: "Das echte Formular, das Ihre Gäste bekommen. Dort gesendete Antworten werden gespeichert.", setQr: 'QR-Code herunterladen', setForm: 'Formular', setEdit: 'Formular bearbeiten', setEditHint: 'Fragen, Design und Übersetzungen', setPreview: 'Vorschau als Gast', setPreviewHint: "Probieren Sie es aus: Nichts, was Sie hier senden, wird gespeichert.", setPreviewTagsHint: "Sehen Sie, was jeder markierte Gast bekommt. Nichts wird gespeichert.", setPlan: 'Tarif', setUpgrade: 'Event upgraden', setUpgradeHint: 'Mehr Antworten, Benachrichtigungen, QR-Check-in und mehr', planFree: 'Kostenloser Tarif · zeigt bis zu {cap} Antworten · Event Pro: {price} einmalig, bis zu 150', planWall: '{cap} von {n} Antworten sichtbar · Event Pro für {price} zeigt alle {n}', planPro: 'Event Pro · Signature bringt Hochzeitswebsite und Sitzplan für {price} mehr', planSee: 'Tarife ansehen', setSiteReg: 'Website und Geschenkliste', setRegistry: 'Geschenkliste', setRegistryHint: 'Geschenke und Beiträge', setWebsite: 'Event-Website', setCopies: 'Kopien', setDuplicate: 'Event duplizieren', setDuplicateHint: 'Eine neue Kopie ohne Antworten', setTemplate: 'Als Vorlage speichern', setBranding: 'Planer-Branding', setDelete: 'Event löschen', setDeleteHint: 'Sie können es direkt danach rückgängig machen', setShared: 'Mit Ihnen geteilt: Nur der Eigentümer kann es duplizieren oder löschen.', replies_one: '{n} Antwort', replies_other: '{n} Antworten', newSince: '+{n} neu', sinceLast: 'Seit du dieses Dashboard zuletzt geöffnet hast', replied: 'Geantwortet', attending: 'Kommen', event_one: 'Event', event_other: 'Events', upcoming: 'Anstehend', repliesTile: 'Antworten', seatingDone: 'Sitzplan fertig', searchEvents: 'Events suchen', noDate: 'Kein Datum', rsvp: 'Rückmeldung {rel}', rsvpClosed: 'Rückmeldung geschlossen {rel}', noReplies: 'Noch keine Antworten.', eventIn: 'Das Event ist {rel}.', rsvpClose: 'Rückmeldungen schließen {rel}.', remind: 'Erinnere die Gäste, die noch nicht geantwortet haben.', remindN: 'Erinnere die Gäste, die noch nicht geantwortet haben ({n}).', seatNow: 'Die Rückmeldungen sind geschlossen. Zeit für den Sitzplan und den Check-in.', ctaInvite: 'Einladungen senden', ctaRemind: 'Erinnerung senden', ctaSeat: 'Sitzplan öffnen', sReplies: 'Antworten', sGuests: 'Gäste', sAdults: 'Erwachsene', sChildren: 'Kinder', sMaybe: 'Vielleicht', sAwaiting: 'Ausstehend', sWaitlisted: 'Warteliste', exportCsv: 'CSV exportieren', partyOf: 'Gruppe von ', looseHead: 'Keinem Event zugeordnet', webWord: "Website", webEdit: "Website bearbeiten", webView: "Website ansehen", webCopy: "Link kopieren", webCopied: "Link kopiert", webLive: "Veröffentlicht", webDraft: "Entwurf", webExpired: "Abgelaufen", webDraftNote: "Noch nicht veröffentlicht. Gäste sehen sie erst, wenn du sie veröffentlichst.", webExpiredNote: "Diese Website ist abgelaufen. Verlängere sie, damit sie wieder online ist.", webPublish: "Veröffentlichen", webRenew: "Verlängern", webSections: "Abschnitt bearbeiten", webNames: "Namen & Datum", webDesign: "Design", webHome: "Startseite", webDetails: "Details", webTravel: "Anreise", webFaq: "FAQ", webPhotos: "Fotos", webRsvp: "RSVP", webAddress: "Website-Adresse", webLangs: "Sprachen", webPreview: "Vorschau", webNudgeTitle: "Füge eine Hochzeitswebsite hinzu", webNudgeDesc: "Eure Geschichte, Ablauf, Anreise und FAQ an einem Ort, mit dem RSVP-Formular dieses Events eingebaut.", webCreate: "Website erstellen", webOwnerOnly: "Dieses Event hat noch keine Website. Nur der Eigentümer kann eine hinzufügen.", webLoadError: "Die Website konnte nicht geladen werden. Versuche es gleich noch einmal.", moreTools: "Weitere Werkzeuge" },
        pt: { guestLink: 'Copiar link', collapseSidebar: 'Recolher a barra lateral', expandSidebar: 'Expandir a barra lateral', signOut: 'Sair', editForm: 'Editar formulário', past: 'Passados', allEvents: 'Todos os eventos', language: 'Idioma', on: 'Ativadas', setSharing: 'Compartilhar', setAccess: 'Coanfitriões e acesso', setAccessHint: 'Deixe outra pessoa gerenciar este evento', setNotif: 'Notificações de respostas', setNotifHint: 'E-mail ou push quando um convidado responde', setOpenForm: 'Abrir o formulário', setOpenFormHint: "O formulário real que seus convidados recebem. Respostas enviadas lá são salvas.", setQr: 'Baixar o QR code', setForm: 'Formulário', setEdit: 'Editar o formulário', setEditHint: 'Perguntas, design e traduções', setPreview: 'Visualizar como convidado', setPreviewHint: "Experimente: nada que você enviar aqui é salvo.", setPreviewTagsHint: "Veja o que cada convidado marcado recebe. Nada é salvo.", setPlan: 'Plano', setUpgrade: 'Fazer upgrade deste evento', setUpgradeHint: 'Mais respostas, avisos de resposta, check-in por QR e mais', planFree: 'Plano grátis · mostra até {cap} respostas · Event Pro: {price} em pagamento único, até 150', planWall: '{cap} de {n} respostas visíveis · O Event Pro por {price} mostra todas as {n}', planPro: 'Event Pro · Signature adiciona site de casamento e mapa de mesas por mais {price}', planSee: 'Ver planos', setSiteReg: 'Site e lista de presentes', setRegistry: 'Lista de presentes', setRegistryHint: 'Presentes e contribuições', setWebsite: 'Site do evento', setCopies: 'Cópias', setDuplicate: 'Duplicar este evento', setDuplicateHint: 'Uma cópia nova, sem respostas', setTemplate: 'Salvar como modelo', setBranding: 'Marca do organizador', setDelete: 'Excluir este evento', setDeleteHint: 'Você pode desfazer logo em seguida', setShared: 'Compartilhado com você: só o proprietário pode duplicar ou excluir.', replies_one: '{n} resposta', replies_other: '{n} respostas', newSince: '+{n} novas', sinceLast: 'Desde a última vez que você abriu este painel', replied: 'Responderam', attending: 'Vão comparecer', event_one: 'Evento', event_other: 'Eventos', upcoming: 'Próximos', repliesTile: 'Respostas', seatingDone: 'Mesas prontas', searchEvents: 'Buscar eventos', noDate: 'Sem data', rsvp: 'Confirmações {rel}', rsvpClosed: 'Confirmações encerradas {rel}', noReplies: 'Ainda não há respostas.', eventIn: 'O evento é {rel}.', rsvpClose: 'As confirmações encerram {rel}.', remind: 'Lembre os convidados que ainda não responderam.', remindN: 'Lembre os convidados que ainda não responderam ({n}).', seatNow: 'As confirmações estão encerradas. Hora de organizar as mesas e preparar o check-in.', ctaInvite: 'Enviar convites', ctaRemind: 'Enviar lembrete', ctaSeat: 'Abrir mesas', sReplies: 'Respostas', sGuests: 'Convidados', sAdults: 'Adultos', sChildren: 'Crianças', sMaybe: 'Talvez', sAwaiting: 'Sem resposta', sWaitlisted: 'Lista de espera', exportCsv: 'Exportar CSV', partyOf: 'Grupo de ', looseHead: 'Sem evento vinculado', webWord: "Site", webEdit: "Editar site", webView: "Ver site", webCopy: "Copiar link", webCopied: "Link copiado", webLive: "Publicado", webDraft: "Rascunho", webExpired: "Expirado", webDraftNote: "Ainda não publicado. Os convidados só vão vê-lo depois que você publicar.", webExpiredNote: "Este site expirou. Renove-o para colocá-lo no ar de novo.", webPublish: "Publicar", webRenew: "Renovar", webSections: "Editar uma seção", webNames: "Nomes e data", webDesign: "Design", webHome: "Início", webDetails: "Detalhes", webTravel: "Viagem", webFaq: "Perguntas frequentes", webPhotos: "Fotos", webRsvp: "RSVP", webAddress: "Endereço do site", webLangs: "Idiomas", webPreview: "Pré-visualização", webNudgeTitle: "Adicione um site de casamento", webNudgeDesc: "Sua história, programação, viagem e perguntas frequentes num só lugar, com o RSVP deste evento já incluído.", webCreate: "Criar site", webOwnerOnly: "Este evento ainda não tem site. Só o proprietário pode adicionar um.", webLoadError: "Não foi possível carregar o site. Tente novamente em instantes.", moreTools: "Mais ferramentas" },
        he: { guestLink: 'העתקת הקישור', collapseSidebar: 'כיווץ סרגל הצד', expandSidebar: 'הרחבת סרגל הצד', signOut: 'התנתקות', editForm: 'עריכת הטופס', past: 'שעברו', allEvents: 'כל האירועים', language: 'שפה', on: 'פעיל', setSharing: 'שיתוף', setAccess: 'מארחים שותפים והרשאות', setAccessHint: 'תנו למישהו נוסף לנהל את האירוע', setNotif: 'התראות על תשובות', setNotifHint: 'מייל או התראה כשאורח עונה', setOpenForm: 'פתיחת הטופס', setOpenFormHint: "הטופס האמיתי שהאורחים מקבלים. תשובות שנשלחות בו נשמרות.", setQr: 'הורדת קוד QR', setForm: 'טופס', setEdit: 'עריכת הטופס', setEditHint: 'שאלות, עיצוב ותרגומים', setPreview: 'תצוגה מקדימה כאורח', setPreviewHint: "נסו אותו: שום דבר שתשלחו כאן לא נשמר.", setPreviewTagsHint: "ראו מה מקבל כל אורח מתויג. שום דבר לא נשמר.", setPlan: 'חבילה', setUpgrade: 'שדרוג האירוע', setUpgradeHint: 'יותר תשובות, התראות, צ׳ק-אין עם QR ועוד', planFree: 'חבילה חינמית · מוצגות עד {cap} תשובות · Event Pro: {price} בתשלום אחד, עד 150', planWall: 'מוצגות {cap} מתוך {n} תשובות · Event Pro תמורת {price} מציג את כל ה-{n}', planPro: 'Event Pro · Signature מוסיף אתר חתונה וסידורי הושבה תמורת {price} נוספים', planSee: 'לצפייה בחבילות', setSiteReg: 'אתר ורשימת מתנות', setRegistry: 'רשימת מתנות', setRegistryHint: 'מתנות והשתתפות', setWebsite: 'אתר האירוע', setCopies: 'עותקים', setDuplicate: 'שכפול האירוע', setDuplicateHint: 'עותק חדש בלי תשובות', setTemplate: 'שמירה כתבנית', setBranding: 'מיתוג המפיק', setDelete: 'מחיקת האירוע', setDeleteHint: 'אפשר לבטל מיד אחרי', setShared: 'שותף איתך: רק הבעלים יכולים לשכפל או למחוק.', replies_one: 'תגובה אחת', replies_other: '{n} תגובות', newSince: '+{n} חדשות', sinceLast: 'מאז הפעם האחרונה שפתחת את לוח הבקרה', replied: 'השיבו', attending: 'מגיעים', event_one: 'אירוע', event_other: 'אירועים', upcoming: 'קרובים', repliesTile: 'תגובות', seatingDone: 'הושיבה מוכנה', searchEvents: 'חיפוש אירועים', noDate: 'ללא תאריך', rsvp: 'סגירת אישורים {rel}', rsvpClosed: 'האישורים נסגרו {rel}', noReplies: 'עדיין אין תגובות.', eventIn: 'האירוע {rel}.', rsvpClose: 'אישורי ההגעה נסגרים {rel}.', remind: 'שלחו תזכורת לאורחים שעדיין לא השיבו.', remindN: 'שלחו תזכורת לאורחים שעדיין לא השיבו ({n}).', seatNow: 'אישורי ההגעה נסגרו. זה הזמן לסדר את הושיבה ולהכין את הצ׳ק-אין.', ctaInvite: 'שליחת הזמנות', ctaRemind: 'שליחת תזכורת', ctaSeat: 'פתיחת הושיבה', sReplies: 'תגובות', sGuests: 'אורחים', sAdults: 'מבוגרים', sChildren: 'ילדים', sMaybe: 'אולי', sAwaiting: 'ממתינים', sWaitlisted: 'רשימת המתנה', exportCsv: 'ייצוא CSV', partyOf: 'קבוצה של ', looseHead: 'לא משויכים לאירוע', webWord: "אתר", webEdit: "עריכת האתר", webView: "צפייה באתר", webCopy: "העתקת קישור", webCopied: "הקישור הועתק", webLive: "פורסם", webDraft: "טיוטה", webExpired: "פג תוקף", webDraftNote: "האתר עוד לא פורסם. האורחים יראו אותו רק אחרי שתפרסמו.", webExpiredNote: "תוקף האתר פג. חדשו אותו כדי שיחזור לאוויר.", webPublish: "פרסום", webRenew: "חידוש", webSections: "עריכת חלק באתר", webNames: "שמות ותאריך", webDesign: "עיצוב", webHome: "דף הבית", webDetails: "פרטים", webTravel: "הגעה", webFaq: "שאלות נפוצות", webPhotos: "תמונות", webRsvp: "אישור הגעה", webAddress: "כתובת האתר", webLangs: "שפות", webPreview: "תצוגה מקדימה", webNudgeTitle: "הוסיפו אתר לחתונה", webNudgeDesc: "הסיפור שלכם, לוח הזמנים, ההגעה והשאלות הנפוצות במקום אחד, עם טופס אישור ההגעה של האירוע הזה בפנים.", webCreate: "יצירת אתר", webOwnerOnly: "לאירוע הזה עוד אין אתר. רק הבעלים שלו יכולים להוסיף אתר.", webLoadError: "לא הצלחנו לטעון את האתר. נסו שוב בעוד רגע.", moreTools: "כלים נוספים" },
        ar: { guestLink: 'نسخ الرابط', collapseSidebar: 'طيّ الشريط الجانبي', expandSidebar: 'توسيع الشريط الجانبي', signOut: 'تسجيل الخروج', editForm: 'تعديل النموذج', past: 'السابقة', allEvents: 'كل الفعاليات', language: 'اللغة', on: 'مفعّلة', setSharing: 'المشاركة', setAccess: 'المضيفون المشاركون والوصول', setAccessHint: 'اسمح لشخص آخر بإدارة هذه الفعالية', setNotif: 'إشعارات الردود', setNotifHint: 'بريد أو إشعار عند رد ضيف', setOpenForm: 'فتح النموذج', setOpenFormHint: "النموذج الفعلي الذي يصل إلى ضيوفك. الردود المرسلة منه تُحفظ.", setQr: 'تنزيل رمز QR', setForm: 'النموذج', setEdit: 'تعديل النموذج', setEditHint: 'الأسئلة والتصميم والترجمات', setPreview: 'معاينة كضيف', setPreviewHint: "جرّبه: لا يُحفظ أي شيء ترسله هنا.", setPreviewTagsHint: "شاهد ما يصل إلى كل ضيف موسوم. لا يُحفظ شيء.", setPlan: 'الخطة', setUpgrade: 'ترقية هذه الفعالية', setUpgradeHint: 'ردود أكثر وتنبيهات وتسجيل حضور برمز QR والمزيد', planFree: 'الخطة المجانية · تعرض حتى {cap} من الردود · Event Pro: {price} دفعة واحدة، حتى 150', planWall: 'تُعرض {cap} من أصل {n} من الردود · تعرض Event Pro مقابل {price} الردود كلها', planPro: 'Event Pro · تضيف Signature موقع زفاف وترتيب المقاعد مقابل {price} إضافية', planSee: 'عرض الخطط', setSiteReg: 'الموقع وقائمة الهدايا', setRegistry: 'قائمة الهدايا', setRegistryHint: 'الهدايا والمساهمات', setWebsite: 'موقع الفعالية', setCopies: 'النسخ', setDuplicate: 'تكرار هذه الفعالية', setDuplicateHint: 'نسخة جديدة بلا ردود', setTemplate: 'حفظ كقالب', setBranding: 'علامة المنظِّم', setDelete: 'حذف هذه الفعالية', setDeleteHint: 'يمكنك التراجع فورًا بعد ذلك', setShared: 'تمت مشاركتها معك: يمكن للمالك فقط تكرارها أو حذفها.', replies_one: 'الردود: {n}', replies_other: 'الردود: {n}', newSince: '+{n} جديدة', sinceLast: 'منذ آخر مرة فتحت فيها لوحة التحكم', replied: 'ردّوا', attending: 'سيحضرون', event_one: 'فعالية', event_other: 'الفعاليات', upcoming: 'القادمة', repliesTile: 'الردود', seatingDone: 'اكتمل ترتيب المقاعد', searchEvents: 'ابحث في الفعاليات', noDate: 'بلا تاريخ', rsvp: 'إغلاق الردود {rel}', rsvpClosed: 'أُغلقت الردود {rel}', noReplies: 'لا توجد ردود بعد.', eventIn: 'موعد الفعالية {rel}.', rsvpClose: 'يُغلق تأكيد الحضور {rel}.', remind: 'ذكّر الضيوف الذين لم يردّوا بعد.', remindN: 'ذكّر الضيوف الذين لم يردّوا بعد ({n}).', seatNow: 'أُغلق تأكيد الحضور. حان وقت ترتيب مقاعد الضيوف وتجهيز تسجيل الوصول.', ctaInvite: 'إرسال الدعوات', ctaRemind: 'إرسال تذكير', ctaSeat: 'فتح ترتيب المقاعد', sReplies: 'الردود', sGuests: 'الضيوف', sAdults: 'البالغون', sChildren: 'الأطفال', sMaybe: 'ربما', sAwaiting: 'بانتظار الرد', sWaitlisted: 'قائمة الانتظار', exportCsv: 'تصدير CSV', partyOf: 'مجموعة من ', looseHead: 'غير مرتبطة بفعالية', webWord: "الموقع", webEdit: "تعديل الموقع", webView: "عرض الموقع", webCopy: "نسخ الرابط", webCopied: "تم نسخ الرابط", webLive: "منشور", webDraft: "مسودة", webExpired: "منتهي", webDraftNote: "لم يُنشر بعد. لن يراه ضيوفك حتى تنشره.", webExpiredNote: "انتهت صلاحية هذا الموقع. جدّده ليعود إلى الإنترنت.", webPublish: "نشر", webRenew: "تجديد", webSections: "تعديل قسم", webNames: "الأسماء والتاريخ", webDesign: "التصميم", webHome: "الرئيسية", webDetails: "التفاصيل", webTravel: "السفر", webFaq: "الأسئلة الشائعة", webPhotos: "الصور", webRsvp: "تأكيد الحضور", webAddress: "عنوان الموقع", webLangs: "اللغات", webPreview: "معاينة", webNudgeTitle: "أضف موقعًا لحفل الزفاف", webNudgeDesc: "قصتكما والبرنامج والسفر والأسئلة الشائعة في مكان واحد، مع نموذج تأكيد الحضور لهذه الفعالية.", webCreate: "إنشاء الموقع", webOwnerOnly: "لا يوجد موقع لهذه الفعالية بعد. يمكن لمالكها فقط إضافة موقع.", webLoadError: "تعذّر تحميل الموقع. حاول مرة أخرى بعد قليل.", moreTools: "أدوات إضافية" },
    };
    const S = STR[LANG] || STR.en;
    const t = (key, vars) => String(S[key] != null ? S[key] : STR.en[key]).replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] != null ? vars[k] : m));
    const plural = (key, n) => {
        let form = 'other';
        try { form = new Intl.PluralRules(LANG).select(n) === 'one' ? 'one' : 'other'; } catch (e) { form = n === 1 ? 'one' : 'other'; }
        return t(key + '_' + form, { n });
    };
    let rtf = null;
    try { rtf = new Intl.RelativeTimeFormat(LANG, { numeric: 'auto' }); } catch (e) { rtf = null; }
    const rel = (days) => (rtf ? rtf.format(days, 'day') : (days === 0 ? 'today' : days > 0 ? `in ${days} days` : `${-days} days ago`));
    const isTablet = () => window.matchMedia('(min-width: 768px) and (max-width: 1100px)').matches;

    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    function currentFormId() {
        return new URLSearchParams(window.location.search).get('formId') || '';
    }

    // ---- The plan, priced, under the event's name (2026-11-03) ---------------
    // Checkouts from the per-event Upgrade pill on All events fell from ~3 a
    // week to under 1 once one-event hosts went straight to their event
    // (openOnlyEvent), and on phones the event's own Upgrade button lives in
    // the rail, behind the menu. This line is visible at every width, carries
    // the price, and shows exactly when that Upgrade button would.
    function renderPlanLine() {
        const sub = $('dv2EventSub');
        const btn = $('header-upgrade-btn');
        let line = $('dv2PlanLine');
        const id = currentFormId();
        const fd = window.currentFormData || {};
        const gates = window.FeatureGates;
        const tier = gates && gates.getTier ? gates.getTier(fd) : null;
        const show = sub && btn && btn.style.display !== 'none' && id && !IS_DEMO
            && !window.viewerMode && !window.isAdminMode && (tier === 'free' || tier === 'event_pro');
        if (!show) { if (line) line.hidden = true; return; }
        const money = (key) => {
            const usd = window.planPriceUSD ? window.planPriceUSD(key, fd) : null;
            if (usd == null) return '';
            return window.formatPrice ? window.formatPrice(usd) : '$' + usd;
        };
        let text;
        if (tier === 'event_pro') {
            text = t('planPro', { price: money('signature_upgrade') });
        } else {
            // At the free reply wall, the host's own numbers (response-limits.js).
            const wall = window.ResponseLimitManager?.wallByForm?.[id];
            if (wall && wall.hidden > 0 && wall.replies > 0 && wall.limit > 0) {
                text = t('planWall', { cap: wall.limit, n: wall.replies, price: money('basic') });
            } else {
                const cap = gates.getEffectiveResponseCap ? gates.getEffectiveResponseCap(fd) : 25;
                text = t('planFree', { cap, price: money('basic') });
            }
        }
        if (!line) {
            line = document.createElement('div');
            line.id = 'dv2PlanLine';
            line.className = 'dv2-plan-line';
            sub.insertAdjacentElement('afterend', line);
            line.addEventListener('click', (e) => {
                if (!e.target.closest('.dv2-plan-line-btn')) return;
                const paid = window.FeatureGates?.getTier?.(window.currentFormData || {}) === 'event_pro';
                // Its own context, so its checkouts are counted apart from the
                // pill and the wall (payment-manager.js tier_chooser_shown).
                window.PaymentManager?.showUpgradeModal?.(currentFormId(), 'plan-line', paid ? 'basic_signature' : 'basic');
            });
        }
        setHtml(line, `<span>${esc(text)}</span><button type="button" class="dv2-plan-line-btn">${esc(t('planSee'))}</button>`);
        line.hidden = false;
    }

    // ---- The event's name heads its page, from the topbar title --------------
    function syncEventName() {
        const title = $('dashboard-title');
        const nameEl = $('dv2EventName');
        if (!title || !nameEl) return;
        // admin.js writes "<event> - Responses"; the rail already names the
        // section, so the topbar shows the event alone. Re-entry is harmless:
        // the second pass finds no suffix.
        const raw = (title.textContent || '').trim();
        // The suffix is translated in the localized dashboards ("… - תשובות"),
        // so prefer the event's own title when the heading carries more.
        const real = String((window.currentFormData && window.currentFormData.title) || '').trim();
        if (real && raw !== real && raw.startsWith(real + ' - ') && window.applyDashboardTitle) {
            window.applyDashboardTitle(title, real);
            return;
        }
        if (SECTION_SUFFIX.test(raw) && window.applyDashboardTitle) {
            window.applyDashboardTitle(title, raw.replace(SECTION_SUFFIX, ''));
            return;
        }
        // dashboard-title.js caps long names and keeps the full one in title=.
        const full = (title.getAttribute('title') || raw).replace(SECTION_SUFFIX, '').trim();
        if (!full || full === 'Dashboard') return;
        nameEl.textContent = full;
        nameEl.title = full;
        if ($('dv2EventList')?.hasAttribute('data-own')) renderEvents([]);
    }

    function formatDate(d) {
        const day = localDay(d);
        return day ? day.toLocaleDateString(LANG, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    }

    // An event date as a LOCAL calendar day. Firestore Timestamps and Dates
    // (currentFormData) keep their local date; strings are read from their
    // parts, as my-projects.js does: new Date('YYYY-MM-DD') / Date.parse is
    // UTC midnight and reads as the day before west of Greenwich (chat
    // 2402558a: a Nov 29 event showed "Nov 28" to a host in Trinidad).
    function localDay(value) {
        let d = value;
        if (d && typeof d.toDate === 'function') d = d.toDate();
        else if (d && typeof d.seconds === 'number') d = new Date(d.seconds * 1000);
        if (d instanceof Date) {
            return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }
        const s = String(d || '').trim();
        // A full ISO instant (rsvpDeadline: the host's 23:59 stored as UTC)
        // is a moment: its day is the local one. Its first ten characters
        // are the UTC day, a day late in the Americas, so a deadline was
        // still "closes today" the day after the form had closed.
        if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
            const at = new Date(s);
            return Number.isNaN(at.getTime()) ? null : new Date(at.getFullYear(), at.getMonth(), at.getDate());
        }
        const p = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return p ? new Date(Number(p[1]), Number(p[2]) - 1, Number(p[3])) : null;
    }

    // ---- The sidebar: the host's events ---------------------------------------
    // Every v2 page lists the host's events in the sidebar (the same set My
    // Projects shows, from GET /api/dashboard-v2/events): upcoming first, by
    // date, undated after; the past folded under "Past" (open while one of
    // them is the page). A search box once there are more than six, and All
    // events (the overview) as the last line. The last list is kept for this
    // tab, so moving between events never flickers.
    const EVENTS_KEY = 'dv2Events';
    const PAST_OPEN = 'dv2PastOpen';
    // Past = before today on the host's own calendar, the one the row's
    // "yesterday" / "in 2 days" chips count on. The UTC date of 24h ago kept
    // yesterday's event under Upcoming until 17:00 in Los Angeles, and an
    // event two days old there at 01:00 in Israel.
    const pastCut = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // A row is the event's name. Date and replies are its tooltip; the one
    // thing drawn beside the name is "+N new", and only when there is news.
    // The open event's news is seen at the host's first click on the page
    // (hereSeen, initEventList); another event's, when it is opened.
    let hereSeen = false;
    function eventRow(e, cur) {
        const here = e.id === cur;
        // The page's own row in a demo or shared view has no count to show.
        const tip = (formatDate(e.eventDate) || t('noDate')) + (e.replies == null ? '' : ' · ' + plural('replies', e.replies));
        const badge = e.replies == null || (here && hereSeen) ? '' : newBadge(here ? newSinceArrival(e.replies) : newSince(e.id, e.replies));
        return `<a class="dv2-ev${here ? ' is-current' : ''}" href="${PREFIX}/dashboard?formId=${encodeURIComponent(e.id)}${IS_DEMO ? '&demo=1' : ''}"${here ? ' aria-current="page"' : ''} data-title="${esc(String(e.title).toLowerCase())}" title="${esc(e.title + ' · ' + tip)}">`
            + `<span class="dv2-ev-name" dir="auto">${esc(e.title)}</span>${badge}</a>`;
    }

    const allEventsLink = (here) => `<a class="dv2-ev-all${here ? ' is-current' : ''}" href="${PREFIX}/dashboard?all=1"${here ? ' aria-current="page"' : ''}><i class="bi bi-grid"></i>${esc(t('allEvents'))}</a>`;

    function renderEvents(events) {
        const box = $('dv2EventList');
        if (!box || !Array.isArray(events)) return;
        // All events IS the list, with its own search: its sidebar marks the
        // page and lists nothing.
        if (body.classList.contains('dv2-home')) { setHtml(box, allEventsLink(true)); return; }
        const cur = currentFormId();
        // A demo or shared view has no account to list from: the event itself.
        // It is redrawn when the name arrives (syncEventName).
        box.toggleAttribute('data-own', !events.length && !!cur);
        if (!events.length && cur) {
            const fd = window.currentFormData || {};
            events = [{ id: cur, title: fd.title || $('dv2EventName')?.textContent || '', eventDate: fd.eventDate || null, replies: null, createdAt: 0 }];
        }
        const cut = pastCut();
        const key = (e) => e.eventDate || '9999-99-99';
        const upcoming = events.filter((e) => !e.eventDate || e.eventDate >= cut)
            .sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : (b.createdAt || 0) - (a.createdAt || 0)));
        const past = events.filter((e) => e.eventDate && e.eventDate < cut).sort((a, b) => (a.eventDate < b.eventDate ? 1 : -1));
        let open = false;
        try { open = localStorage.getItem(PAST_OPEN) === '1'; } catch (e) { /* private mode */ }
        if (past.some((e) => e.id === cur)) open = true;
        let html = '';
        if (events.length > 6) html += `<input type="search" class="dv2-ev-search" placeholder="${esc(t('searchEvents'))}" aria-label="${esc(t('searchEvents'))}">`;
        html += `<div class="dv2-ev-group">${upcoming.map((e) => eventRow(e, cur)).join('')}</div>`;
        if (past.length) {
            html += `<button type="button" class="dv2-ev-past-toggle" aria-expanded="${open}"><i class="bi bi-chevron-right"></i>${esc(t('past'))}<span class="dv2-ev-count">${past.length}</span></button>`
                + `<div class="dv2-ev-group dv2-ev-past"${open ? '' : ' hidden'}>${past.map((e) => eventRow(e, cur)).join('')}</div>`;
        }
        if (!IS_DEMO) html += allEventsLink(false);
        setHtml(box, html);
        const toggle = box.querySelector('.dv2-ev-past-toggle');
        const pastBox = box.querySelector('.dv2-ev-past');
        if (toggle) toggle.onclick = () => {
            const now = pastBox.hidden;
            pastBox.hidden = !now;
            toggle.setAttribute('aria-expanded', String(now));
            try { localStorage.setItem(PAST_OPEN, now ? '1' : '0'); } catch (e) { /* private mode */ }
        };
        const search = box.querySelector('.dv2-ev-search');
        if (search) search.oninput = () => {
            const q = search.value.trim().toLowerCase();
            box.querySelectorAll('.dv2-ev').forEach((a) => { a.hidden = !!q && !a.dataset.title.includes(q); });
            // While searching, the past is searched too.
            if (pastBox) pastBox.hidden = !q && toggle.getAttribute('aria-expanded') !== 'true';
        };
        box.querySelector('.dv2-ev.is-current')?.scrollIntoView({ block: 'nearest' });
    }

    // Most hosts have one event: from the home they go straight to it, unless
    // they asked for the home (?all=1, the All events link) or arrived with an
    // action for it (?credits=, ?registry=new, ?notify= ...). Tracking tags
    // are not a request: every lifecycle email's link carries utm_source=
    // lifecycle, and stopped there. They travel on to the event. Nor is ?all=1
    // when the host signed in on this page: that was a signed-out visit's
    // address, and signing in is arriving. Judged on the address the page
    // OPENED with: My Projects strips ?credits= / ?upgrade= / ?notify= once
    // their window is open, and this page strips ?act=, all before
    // the list arrives, and jumping then closed the window they came for.
    // Only an ARRIVAL jumps (task #927): once this tab has shown one of the
    // host's events, coming back to the home is asking for the home. Without
    // that, every way back from the event that lacked ?all=1 (the locked-
    // responses window's "Back to all events", a chatbot link) bounced
    // straight back into it, and for a host over the free limit re-opened the
    // upgrade window in a loop. Per tab: a new tab is a new arrival.
    const SEEN_EVENT_KEY = 'dv2SeenEvent';
    const TRACKING = /^(utm_\w+|fbclid|gclid|msclkid|_gl)$/;
    const ARRIVAL_QUERY = window.location.search;
    function openOnlyEvent(events, signedInHere) {
        if (!body.classList.contains('dv2-home') || events.length !== 1) return;
        try { if (sessionStorage.getItem(SEEN_EVENT_KEY) === '1') return; } catch (e) { /* private mode: arrival rules only */ }
        const params = new URLSearchParams(ARRIVAL_QUERY);
        if (signedInHere) params.delete('all');
        if ([...params.keys()].some((k) => !TRACKING.test(k))) return;
        params.set('formId', events[0].id);
        // The fetches My Projects has in flight are cancelled by this and
        // log 'Failed to fetch' / 'Load failed'; utils/error-buffer.js drops
        // those once the page is leaving. iOS Safari fires no beforeunload,
        // so say so here.
        window.__qrLeavingAt = Date.now();
        window.location.replace(`${PREFIX}/dashboard?${params}`);
    }

    function initEventList() {
        if (!$('dv2EventList')) return;
        // Any click is the host looking at the page, except the phone's menu
        // button: that tap is what shows them the list, badge and all.
        if (currentFormId()) {
            const seen = (e) => {
                if (e.target.closest && e.target.closest('#dv2TabsMenuBtn, #dv2MenuBtn')) return;
                hereSeen = true;
                document.querySelector('#dv2EventList .dv2-ev.is-current .dv2-new')?.remove();
                document.removeEventListener('pointerdown', seen, true);
            };
            document.addEventListener('pointerdown', seen, true);
        }
        // The demo is the sample event alone, signed in or not: a visitor's
        // own events beside it would open in demo mode, read-only.
        if (IS_DEMO) { renderEvents([]); return; }
        try {
            const cached = JSON.parse(sessionStorage.getItem(EVENTS_KEY) || 'null');
            if (cached && Array.isArray(cached.events)) renderEvents(cached.events);
        } catch (e) { /* private mode */ }
        const fb = window.firebase;
        if (!fb || !fb.auth || !fb.apps || !fb.apps.length) { renderEvents([]); return; }
        // Signed out and anonymous (a form started before signing up) are the
        // same here: no events, and keep listening for the real sign-in.
        let signedInHere = false;
        const off = fb.auth().onAuthStateChanged(async (user) => {
            if (!user || user.isAnonymous) { signedInHere = true; renderEvents([]); return; }
            off();
            try {
                const token = await user.getIdToken();
                const r = await fetch('/api/dashboard-v2/events', { headers: { Authorization: 'Bearer ' + token } });
                if (!r.ok) return;
                const { events } = await r.json();
                try { sessionStorage.setItem(EVENTS_KEY, JSON.stringify({ uid: user.uid, events })); } catch (e) { /* private mode */ }
                renderEvents(events);
                openOnlyEvent(events, signedInHere);
            } catch (e) { /* the cached list stays */ }
        });
    }

    // "+ New": event, website or registry.
    function setNewMenu(open) {
        const btn = $('dv2NewBtn'), menu = $('dv2NewMenu');
        if (!btn || !menu) return;
        menu.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
    }

    // ---- Phones: the sections run sideways in the top bar; keep the open one
    // in view (scrolling the bar only, never the page).
    const barScroller = () => $('dashboardTabs');
    function showActiveSection() {
        const nav = barScroller();
        const link = nav && nav.querySelector('.nav-link.active');
        if (!link || !isPhone() || nav.scrollWidth <= nav.clientWidth) return;
        const n = nav.getBoundingClientRect();
        const l = link.getBoundingClientRect();
        nav.scrollBy({ left: (l.left + l.width / 2) - (n.left + n.width / 2) });
    }

    // ---- Sideways scrollers say there is more ---------------------------------
    // The edge with content behind it fades out: the phone section bar (Guest
    // Photos wholly off a 375px screen) and every wide table (the responses
    // table's later columns). data-more = start | end | both, in reading
    // order; CSS maps it to a side per direction. RTL scrollLeft runs
    // 0 → negative, hence abs().
    function markEdges(el, active) {
        const max = el.scrollWidth - el.clientWidth;
        if (!active || max <= 1) { delete el.dataset.more; return; }
        const pos = Math.abs(el.scrollLeft);
        const start = pos > 1, end = pos < max - 1;
        const v = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
        if (el.dataset.more !== v) { if (v) el.dataset.more = v; else delete el.dataset.more; }
    }
    // Content arrives after this runs (the localizer rewrites the bar's
    // labels; admin.js redraws a table wholesale), so measure on scroll and
    // whenever the scroller or anything directly inside it changes size.
    function watchEdges(el, isActive) {
        if (!el || el.dataset.dv2Edges) return;
        el.dataset.dv2Edges = '1';
        const mark = () => markEdges(el, isActive());
        el.addEventListener('scroll', mark, { passive: true });
        if (window.ResizeObserver) {
            const ro = new ResizeObserver(mark);
            const watchChildren = () => [...el.children].forEach((c) => ro.observe(c));
            ro.observe(el);
            watchChildren();
            new MutationObserver(() => { watchChildren(); mark(); }).observe(el, { childList: true });
        } else window.addEventListener('resize', mark);
        mark();
    }
    function initBarOverflow() {
        watchEdges(barScroller(), isPhone);
    }
    // Tables are drawn by each section's script, after this runs and again on
    // every redraw (admin.js replaces the responses table's container), so new
    // scrollers are picked up as they appear, not scanned for once. Batched
    // with setTimeout, not requestAnimationFrame (which never fires in a
    // background tab).
    function watchTables() {
        const root = $('dashboardTabContent');
        if (!root || root.dataset.dv2Tables) return;
        root.dataset.dv2Tables = '1';
        let queued = false;
        const scan = () => {
            queued = false;
            // A redraw that replaced only the scroller leaves its bar behind.
            root.querySelectorAll('.dv2-hscroll').forEach((b) => {
                const t = b.previousElementSibling;
                if (!t || !t.classList.contains('table-responsive')) b.remove();
            });
            root.querySelectorAll('.table-responsive:not([data-dv2-edges])').forEach(watchTable);
        };
        new MutationObserver(() => { if (!queued) { queued = true; setTimeout(scan, 0); } })
            .observe(root, { childList: true, subtree: true });
        scan();
    }

    // ---- Wide tables: a pinned scrollbar; no fade over sticky columns -------
    // A wide table's own scrollbar sits under its last row, often a screen
    // below, so it is hidden and one is drawn under the table instead: sticky
    // at the bottom of the viewport, settling under the table once its end is
    // in view. Drawn (track + thumb), not a second native bar, because overlay
    // scrollbars (Firefox, Windows 11) take no height until hovered. The edge
    // fade skips a side with sticky columns (# / Name, Actions), whose own
    // edge shadow says there is more. Sides are physical here: the pane can be
    // RTL inside an LTR page, and RTL scrollLeft runs 0 → negative.
    function stickySides(el) {
        const row = el.querySelector(':scope > table > thead > tr');
        let start = false, end = false;
        if (row) {
            for (const th of row.children) {
                const cs = getComputedStyle(th);
                if (cs.position !== 'sticky') continue;
                if (cs.insetInlineStart !== 'auto') start = true; else end = true;
            }
        }
        const rtl = getComputedStyle(el).direction === 'rtl';
        return { rtl, left: rtl ? end : start, right: rtl ? start : end };
    }
    function watchTable(el) {
        if (el.dataset.dv2Edges) return;
        el.dataset.dv2Edges = '1';
        const old = el.nextElementSibling;
        if (old && old.classList.contains('dv2-hscroll')) old.remove();
        const bar = document.createElement('div');
        bar.className = 'dv2-hscroll';
        bar.setAttribute('aria-hidden', 'true');
        const track = document.createElement('div');
        track.className = 'dv2-hscroll-track';
        const thumb = document.createElement('div');
        thumb.className = 'dv2-hscroll-thumb';
        track.appendChild(thumb);
        bar.appendChild(track);
        el.after(bar);
        let sides = { rtl: false, left: false, right: false };
        let thumbW = 0;
        const maxScroll = () => el.scrollWidth - el.clientWidth;
        // pos: how far from the reading start, 0..max, in either direction.
        const setPos = (pos) => {
            const p = Math.max(0, Math.min(maxScroll(), pos));
            el.scrollLeft = sides.rtl ? -p : p;
        };
        const mark = () => {
            const max = maxScroll();
            const over = max > 1;
            el.classList.toggle('dv2-wide', over);
            bar.hidden = !over;
            if (!over) { delete el.dataset.more; return; }
            const pos = Math.abs(el.scrollLeft);
            const start = pos > 1, end = pos < max - 1;
            const v = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
            if (el.dataset.more !== v) { if (v) el.dataset.more = v; else delete el.dataset.more; }
            const leftMore = sides.rtl ? end : start, rightMore = sides.rtl ? start : end;
            el.style.setProperty('--dv2-al', leftMore && !sides.left ? 0 : 1);
            el.style.setProperty('--dv2-ar', rightMore && !sides.right ? 0 : 1);
            const room = track.clientWidth - thumbW;
            thumb.style.insetInlineStart = (room * Math.min(1, pos / max)) + 'px';
        };
        const measure = () => {
            sides = stickySides(el);
            bar.style.direction = sides.rtl ? 'rtl' : 'ltr';
            bar.hidden = maxScroll() <= 1;
            thumbW = Math.max(40, track.clientWidth * el.clientWidth / el.scrollWidth);
            thumb.style.width = thumbW + 'px';
            mark();
        };
        el.addEventListener('scroll', mark, { passive: true });
        // Drag the thumb; a press on the track jumps a screenful toward it.
        const ratio = () => maxScroll() / Math.max(1, track.clientWidth - thumbW);
        const dir = () => (sides.rtl ? -1 : 1);
        thumb.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            thumb.setPointerCapture(e.pointerId);
            thumb.classList.add('is-dragging');
            const x0 = e.clientX, pos0 = Math.abs(el.scrollLeft), k = ratio();
            const move = (ev) => setPos(pos0 + (ev.clientX - x0) * dir() * k);
            const up = () => {
                thumb.classList.remove('is-dragging');
                thumb.removeEventListener('pointermove', move);
                thumb.removeEventListener('pointerup', up);
                thumb.removeEventListener('pointercancel', up);
            };
            thumb.addEventListener('pointermove', move);
            thumb.addEventListener('pointerup', up);
            thumb.addEventListener('pointercancel', up);
        });
        track.addEventListener('pointerdown', (e) => {
            if (e.button !== 0 || e.target === thumb) return;
            const t = thumb.getBoundingClientRect();
            const after = sides.rtl ? e.clientX < t.left : e.clientX > t.right;
            setPos(Math.abs(el.scrollLeft) + (after ? 1 : -1) * el.clientWidth * .8);
        });
        if (window.ResizeObserver) {
            const ro = new ResizeObserver(measure);
            const watchChildren = () => [...el.children].forEach((c) => ro.observe(c));
            ro.observe(el);
            ro.observe(track);
            watchChildren();
            new MutationObserver(() => { watchChildren(); measure(); }).observe(el, { childList: true });
        } else window.addEventListener('resize', measure);
        measure();
    }

    // ---- Phone sheet -------------------------------------------------------
    function setSheet(open) {
        body.classList.toggle('dv2-sheet-open', open);
        const scrim = $('dv2Scrim');
        if (scrim) scrim.hidden = !open;
        $('dv2MenuBtn')?.setAttribute('aria-expanded', String(open));
        $('dv2TabsMenuBtn')?.setAttribute('aria-expanded', String(open));
        // The event list starts closed: open, it pushed This event and the
        // footer below the fold on a phone. One tap on the event card opens it.
        if (!open) setNewMenu(false);
    }

    // ---- All events home: rows + totals -------------------------------------
    // my-projects.js still draws every card (badges, lock warning, Upgrade,
    // website/registry slots, actions); CSS lays each card out as a row. This
    // only adds what a row shows and a card did not: the reply count up front,
    // Attending when the portfolio stats are in, and a totals strip.
    function daysUntil(value) {
        const a = localDay(value);
        if (!a) return null;
        const now = new Date();
        const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return Math.round((a - b) / 864e5);
    }

    // Replies, not rows: responseCount also counts an uploaded guest list.
    const repliesOf = (f) => (typeof f._liveResponseCount === 'number' ? f._liveResponseCount
      : window.FeatureGates?.getRepliesReceived ? window.FeatureGates.getRepliesReceived(f) : (f.responseCount || 0));
    const attendingOf = (f) => (f._stats && typeof f._stats.attending === 'number' ? f._stats.attending : null);
    const statHtml = (value, label, cls, raw) =>
        `<span class="dv2-row-stat${cls ? ' ' + cls : ''}"><b>${raw ? value : esc(value)}</b><small>${esc(label)}</small></span>`;

    // Studio-style row from the card my-projects.js draws. Done once per
    // card (the list is redrawn wholesale, so a fresh card gets it again):
    //   head  = the name, now a link to the dashboard
    //   meta  = date chip, RSVP chip, then the card's own badges (plan, Upgrade,
    //           Closed, Shared…), one line
    //   stats = Replied / Attending (filled by decorateHome)
    //   links = website and registry slots (the card's own)
    //   acts  = the form's icon actions + Edit + Delete, one row
    // The form box ("RSVP form · 51/25 responses") and the Dashboard / Edit
    // buttons go: the row is the link, Edit is an icon, the count is a stat.
    function shapeRow(card, cardBody, f, m) {
        if (card.dataset.dv2Row) return;
        card.dataset.dv2Row = '1';
        card.classList.add('dv2-row');
        const dash = m._getDashboardLink(f.id);

        const titleRow = cardBody.querySelector(':scope > .d-flex.justify-content-between');
        const title = titleRow && titleRow.querySelector('.card-title');
        if (title && !title.querySelector('a')) {
            const a = document.createElement('a');
            a.className = 'dv2-row-title';
            a.href = dash;
            while (title.firstChild) a.appendChild(title.firstChild);
            title.appendChild(a);
        }
        if (titleRow) titleRow.classList.add('dv2-row-head');

        const meta = document.createElement('div');
        meta.className = 'dv2-row-meta';
        meta.innerHTML = '<span class="dv2-row-dates"></span>';
        const badges = cardBody.querySelector(':scope > div.mb-2:not(.d-flex)');
        if (badges) { badges.classList.add('dv2-row-badges'); meta.appendChild(badges); }
        // Per-event stat pills (RSVP'd %, Seating done, Site live) join the chips.
        const statRow = cardBody.querySelector('.mp-stat-row');
        if (statRow) meta.appendChild(statRow);
        (titleRow || cardBody.firstChild).insertAdjacentElement('afterend', meta);

        const stats = document.createElement('a');
        stats.className = 'dv2-row-stats';
        meta.insertAdjacentElement('afterend', stats);

        const acts = card.querySelector('.mp-asset-form .mp-asset-acts');
        const buttons = cardBody.querySelector(':scope > .d-flex.gap-2:not(.justify-content-between)');
        const edit = buttons && buttons.querySelector('a[href*="builder"]');
        const trash = cardBody.querySelector('.mp-trash-btn');
        if (acts) {
            acts.classList.add('dv2-row-acts');
            if (edit) { edit.className = 'mp-act'; edit.innerHTML = '<i class="bi bi-pencil"></i>'; acts.appendChild(edit); }
            if (trash) { trash.className = 'mp-act dv2-act-danger'; acts.appendChild(trash); }
            cardBody.appendChild(acts);
        }
        if (buttons) buttons.remove();
    }

    // A website or registry card on its own: name | kind, status, date, address
    // | the "attach to an event" select | its icon buttons (+ Delete) | the
    // primary button and Edit. Same idea as shapeRow, on a card with no form.
    function shapeLooseRow(card, cardBody) {
        if (card.dataset.dv2Row) return;
        card.dataset.dv2Row = '1';
        card.classList.add('dv2-row', 'dv2-row-loose');

        const titleRow = cardBody.querySelector(':scope > .d-flex.justify-content-between');
        if (titleRow) titleRow.classList.add('dv2-row-head');

        const meta = document.createElement('div');
        meta.className = 'dv2-row-meta';
        const badges = cardBody.querySelector(':scope > div.mb-2.d-flex:not(.justify-content-between)');
        const metaLine = cardBody.querySelector(':scope > .mp-meta');
        if (badges) { badges.classList.add('dv2-row-badges'); meta.appendChild(badges); }
        if (metaLine) meta.appendChild(metaLine);
        (titleRow || cardBody.firstChild).insertAdjacentElement('afterend', meta);

        const icons = cardBody.querySelector(':scope > .mp-secondary-row');
        const trash = cardBody.querySelector('.mp-trash-btn');
        if (icons) {
            icons.classList.add('dv2-row-acts');
            if (trash) { trash.className = 'btn btn-sm btn-outline-danger dv2-act-danger'; icons.appendChild(trash); }
        }
        const cta = [...cardBody.querySelectorAll(':scope > .d-flex.gap-2')].pop();
        if (cta && cta !== icons && cta !== titleRow) cta.classList.add('dv2-row-cta');
    }

    function dateChips(f) {
        const toEvent = daysUntil(f.eventDate);
        const toDeadline = daysUntil(f.rsvpDeadline);
        let html = '';
        if (toEvent != null) {
            html += `<span class="dv2-chip"><i class="bi bi-calendar3"></i>${esc(formatDate(f.eventDate))} · ${esc(rel(toEvent))}</span>`;
        } else {
            html += `<span class="dv2-chip is-muted"><i class="bi bi-calendar3"></i>${esc(t('noDate'))}</span>`;
        }
        if (toDeadline != null) {
            const cls = toDeadline < 0 ? ' is-past' : toDeadline <= 7 ? ' is-soon' : '';
            html += `<span class="dv2-chip dv2-chip-rsvp${cls}"><i class="bi bi-clock"></i>${esc(t(toDeadline < 0 ? 'rsvpClosed' : 'rsvp', { rel: rel(toDeadline) }))}</span>`;
        }
        return html;
    }

    function setHtml(el, html) { if (el.innerHTML !== html) el.innerHTML = html; }

    // ---- "+N new": replies since this browser last opened the dashboard ------
    // Per browser, no server work; the worst a stale value costs is a badge.
    const seenKey = (id) => 'dv2seen:' + id;
    function seenCount(id) {
        try { const v = localStorage.getItem(seenKey(id)); return v == null ? null : Number(v); } catch (e) { return null; }
    }
    function rememberSeen(id, n) { try { localStorage.setItem(seenKey(id), String(n)); } catch (e) { /* private mode */ } }
    function newSince(id, n) { const s = seenCount(id); return s == null || !(n > s) ? 0 : n - s; }
    const newBadge = (n) => (n > 0 ? `<span class="dv2-new" title="${esc(t('sinceLast'))}">${esc(t('newSince', { n }))}</span>` : '');
    // The open event's page records its count as soon as its replies load, so
    // its own news is measured from what was seen before this visit.
    const ARRIVAL_SEEN = (() => { const id = currentFormId(); return id ? seenCount(id) : null; })();
    const newSinceArrival = (n) => (ARRIVAL_SEEN == null || !(n > ARRIVAL_SEEN) ? 0 : n - ARRIVAL_SEEN);

    // ---- What to do next ---------------------------------------------------
    // One line per event, from where it is in its life: invitations out, then
    // replies in, then the day itself. Quiet when things are on track.
    // messaged: the event has sent through us (firstMessageSentAt, stamped by
    // the server). No replies yet is then waiting, not a reason to invite:
    // the line only speaks again when RSVPs are about to close.
    function nextStep({ replies, awaiting, eventDate, rsvpDeadline, messaged }) {
        const toEvent = daysUntil(eventDate);
        const toDeadline = daysUntil(rsvpDeadline);
        if (toEvent != null && toEvent < 0) return null;
        if (!replies && !messaged) {
            const when = toEvent == null ? '' : ' ' + t('eventIn', { rel: rel(toEvent) });
            return { kind: 'invite', text: t('noReplies') + when, label: t('ctaInvite'), tab: 'campaigns', open: 'new-message' };
        }
        const closing = toDeadline != null && toDeadline >= 0 && toDeadline <= 7;
        const soon = toDeadline == null && toEvent != null && toEvent <= 7;
        if (closing || soon) {
            const when = closing ? t('rsvpClose', { rel: rel(toDeadline) }) : t('eventIn', { rel: rel(toEvent) });
            const who = awaiting > 0 ? t('remindN', { n: awaiting }) : t('remind');
            // 'remind', not a choice made here: awaiting counts uploaded
            // guest-list rows, not guests we messaged (the home has neither).
            return { kind: 'remind', text: when + ' ' + who, label: t('ctaRemind'), tab: 'campaigns', open: 'remind' };
        }
        if (replies && toDeadline != null && toDeadline < 0) {
            return { kind: 'seat', text: t('seatNow'), label: t('ctaSeat'), tab: 'seating' };
        }
        return null;
    }
    const stepIcon = (s) => ({ invite: 'bi-send', remind: 'bi-bell', seat: 'bi-diagram-3' }[s.kind] || 'bi-arrow-right');

    // A host can put the line away (×) for an event. It stays away until the
    // step itself changes (invitations → reminders → seating), which is new
    // advice; one key per event, shared by the dashboard and the home row.
    const NEXT_HIDDEN = (id) => 'dv2NextHidden:' + id;
    function nextHidden(id, kind) {
        try { return !!id && localStorage.getItem(NEXT_HIDDEN(id)) === kind; } catch (e) { return false; }
    }
    function hideNext(id, kind) {
        try { localStorage.setItem(NEXT_HIDDEN(id), kind); } catch (e) { /* private mode */ }
    }
    const nextClose = () => `<button type="button" class="dv2-next-close" aria-label="${esc(t('fbClose'))}" title="${esc(t('fbClose'))}"><i class="bi bi-x-lg"></i></button>`;

    function renderNextStep(step) {
        const box = $('dv2NextStep');
        if (!box) return;
        const id = currentFormId();
        if (!step || window.viewerMode || nextHidden(id, step.kind)) { box.hidden = true; return; }
        box.hidden = false;
        setHtml(box, `<i class="bi ${stepIcon(step)}"></i><span>${esc(step.text)}</span><button type="button" class="dv2-next-cta">${esc(step.label)}</button>${nextClose()}`);
        box.querySelector('.dv2-next-cta').onclick = () => window.dashboardDeepLink && window.dashboardDeepLink(step.tab, step.open);
        box.querySelector('.dv2-next-close').onclick = () => { hideNext(id, step.kind); box.hidden = true; };
    }

    function decorateHome() {
        const m = window.myProjects;
        const list = $('formsList');
        if (!m || !Array.isArray(m.forms) || !list) return;
        const byId = new Map(m.forms.map((f) => [f.id, f]));

        list.querySelectorAll('.mp-form-card[data-form-id]').forEach((card) => {
            const f = byId.get(card.getAttribute('data-form-id'));
            const cardBody = card.querySelector('.card-body');
            if (!f || !cardBody) return;
            shapeRow(card, cardBody, f, m);
            const box = cardBody.querySelector(':scope > .dv2-row-stats');
            box.href = m._getDashboardLink(f.id);
            const replies = repliesOf(f);
            const att = attendingOf(f);
            // "51/25": the plan's reply limit, as the form box used to say it.
            const limit = (card.querySelector('.mp-asset-form .mp-asset-text small')?.textContent.match(/\d+\s*\/\s*(\d+)/) || [])[1];
            const repliesHtml = limit ? `${replies}<small>/${limit}</small>` : String(replies);
            setHtml(box, statHtml(repliesHtml, t('replied'), '', true) + newBadge(newSince(f.id, replies)) + (att == null ? '' : statHtml(att, t('attending'), 'is-ok')));
            setHtml(cardBody.querySelector(':scope > .dv2-row-meta > .dv2-row-dates'), dateChips(f));

            // The home does not know who is still to answer; the dashboard does.
            const step = nextStep({ replies, awaiting: 0, eventDate: f.eventDate, rsvpDeadline: f.rsvpDeadline, messaged: !!f.firstMessageSentAt });
            let line = cardBody.querySelector(':scope > .dv2-next');
            if (!step || nextHidden(f.id, step.kind)) { if (line) line.remove(); return; }
            if (!line) { line = document.createElement('div'); line.className = 'dv2-next'; cardBody.appendChild(line); }
            const href = m._getDashboardLink(f.id, '&tab=' + step.tab + (step.open ? '&open=' + step.open : ''));
            setHtml(line, `<i class="bi ${stepIcon(step)}"></i><span>${esc(step.text)}</span><a class="dv2-next-cta" href="${esc(href)}">${esc(step.label)}</a>${nextClose()}`);
            line.querySelector('.dv2-next-close').onclick = () => { hideNext(f.id, step.kind); line.remove(); };
        });

        // Websites and registries on their own (not filed under an event)
        // are cards too; same treatment, one row each.
        list.querySelectorAll('.mp-form-card:not([data-form-id])').forEach((card) => {
            const cardBody = card.querySelector('.card-body');
            if (cardBody) shapeLooseRow(card, cardBody);
        });
        groupLoose(list);

        // Totals: only worth a strip once there is more than one event.
        const forms = m.forms;
        let strip = $('dv2HomeSummary');
        if (forms.length < 2) { if (strip) strip.hidden = true; return; }
        if (!strip) {
            strip = document.createElement('div');
            strip.id = 'dv2HomeSummary';
            strip.className = 'dv2-home-summary';
            list.parentNode.insertBefore(strip, list);
        }
        strip.hidden = false;
        const upcoming = forms.filter((f) => { const d = daysUntil(f.eventDate); return d != null && d >= 0; }).length;
        const replies = forms.reduce((n, f) => n + repliesOf(f), 0);
        const atts = forms.map(attendingOf);
        const attending = atts.every((a) => a != null) ? atts.reduce((n, a) => n + a, 0) : null;
        const tile = (icon, value, label, cls) =>
            `<span class="dv2-home-tile${cls ? ' ' + cls : ''}"><i class="bi ${icon}"></i><b>${esc(value)}</b><small>${esc(label)}</small></span>`;
        // Seating done comes with the portfolio stats my-projects.js loads for
        // 3+ events (its own tile strip is hidden on v2; this strip is the one).
        const p = m._portfolio;
        const seated = p && p.events ? `${p.seatingComplete || 0}/${p.events}` : null;
        setHtml(strip,
            tile('bi-calendar-event', forms.length, t(forms.length === 1 ? 'event_one' : 'event_other'))
            + tile('bi-hourglass-split', upcoming, t('upcoming'))
            + tile('bi-envelope-check', replies, t('repliesTile'))
            + (attending == null ? '' : tile('bi-people', attending, t('attending'), 'is-ok'))
            + (seated == null ? '' : tile('bi-diagram-3', seated, t('seatingDone'))));

        initSearch(forms.length);
    }

    // Events first, then the websites and registries not filed under one, in
    // a group of their own at the end (as the planner Studio does). Mixed in by
    // date, a draft website could head the list, and a loose card beside a
    // wedding read as that wedding's. With nothing but loose items, or no
    // loose items, there is no heading. A pure reorder of my-projects.js's
    // columns: it moves nodes only when the order is wrong, so the list
    // observer that runs this settles instead of looping.
    function groupLoose(list) {
        const row = list.querySelector(':scope > .row');
        if (!row) return;
        let head = row.querySelector(':scope > .dv2-loose-head');
        const cols = [...row.children].filter((c) => c !== head);
        const isLoose = (c) => !!c.querySelector('.mp-form-card:not([data-form-id])');
        const loose = cols.filter(isLoose);
        const rest = cols.filter((c) => !isLoose(c));
        if (!loose.length || !rest.length) { if (head) head.remove(); return; }
        if (!head) { head = document.createElement('div'); head.className = 'col-12 dv2-loose-head'; }
        setHtml(head, `<span>${esc(t('looseHead'))}</span><b>${loose.length}</b>`);
        const want = [...rest, head, ...loose];
        if (want.some((el, i) => row.children[i] !== el)) want.forEach((el) => row.appendChild(el));
    }

    // A search box once the list is long enough to need one; it narrows the
    // rows by name as you type.
    function initSearch(count) {
        let box = $('dv2Search');
        if (count < SEARCH_FROM) { if (box) box.hidden = true; return; }
        if (!box) {
            const row = $('formsTabUtilityRow');
            if (!row) return;
            box = document.createElement('input');
            box.id = 'dv2Search';
            box.type = 'search';
            box.className = 'form-control form-control-sm dv2-search';
            box.placeholder = t('searchEvents');
            box.setAttribute('aria-label', t('searchEvents'));
            row.insertAdjacentElement('afterbegin', box);
            box.addEventListener('input', () => {
                const q = box.value.trim().toLowerCase();
                document.querySelectorAll('#formsList .mp-form-card').forEach((card) => {
                    const name = (card.querySelector('.card-title')?.textContent || '').toLowerCase();
                    const col = card.parentElement;
                    (col.className.includes('col') ? col : card).style.display = !q || name.includes(q) ? '' : 'none';
                });
                // The "Not linked to an event" heading goes with its rows.
                const head = document.querySelector('#formsList .dv2-loose-head');
                if (head) {
                    const shown = [...document.querySelectorAll('#formsList .mp-form-card:not([data-form-id])')]
                        .some((c) => c.parentElement.style.display !== 'none');
                    head.style.display = shown ? '' : 'none';
                }
            });
        }
        box.hidden = false;
    }

    // The rail footer takes two things my-projects.js draws into the page:
    // the credits pill (into #dv2CreditsSlot) and the account menu (already
    // in #authNavItem, which the build moved into the rail; its dropdown opens
    // upward there so it is not cut off by the bottom of the screen).
    // Collapsed, the rail is icons: each carries its label as a tooltip and an
    // accessible name. Open, the words are on screen and those tips come off
    // again. A control with a title of its own keeps it; the collapse button
    // says which way it goes.
    const RAIL_ITEMS = '.dv2-brand, .dv2-new-btn, .dv2-rail-link, .dv2-guestlink, #dv2CreditsSlot .mp-credits-pill, .dv2-rail-planner .planner-nav-pill, .dv2-rail-planner .planner-nav-badge, .dv2-rail-auth .btn';
    function tipRail() {
        const rail = $('dv2Rail');
        if (!rail) return;
        const narrow = body.classList.contains('dv2-narrow');
        rail.querySelectorAll(RAIL_ITEMS).forEach((el) => {
            if (narrow) {
                if (el.title && !el.dataset.dv2Tip) return;
                // textContent: the label is display:none here, so innerText is empty.
                const label = el.textContent.replace(/\s+/g, ' ').trim();
                if (!label) return;
                if (el.title !== label) el.title = label;
                el.dataset.dv2Tip = '1';
                if (!el.hasAttribute('aria-label') || el.dataset.dv2TipAria) { el.setAttribute('aria-label', label); el.dataset.dv2TipAria = '1'; }
            } else if (el.dataset.dv2Tip) {
                el.removeAttribute('title');
                delete el.dataset.dv2Tip;
                if (el.dataset.dv2TipAria) { el.removeAttribute('aria-label'); delete el.dataset.dv2TipAria; }
            }
        });
        const c = $('dv2CollapseBtn');
        if (c) {
            const label = t(narrow ? 'expandSidebar' : 'collapseSidebar');
            if (c.title !== label) { c.title = label; c.setAttribute('aria-label', label); }
        }
    }

    function placeRailWidgets() {
        const pill = document.querySelector('#formsTabUtilityRow .mp-credits-pill, #mpCreditBalance');
        const slot = $('dv2CreditsSlot');
        if (pill && slot && pill.parentElement !== slot) slot.appendChild(pill);
        const dd = document.querySelector('#authNavItem .dropdown');
        if (dd && !dd.classList.contains('dropup')) dd.classList.add('dropup');
        // The menu's first item is "My Projects", this same page; here that
        // is "All events", already at the top of the rail.
        document.querySelector('#authNavItem .dropdown-item[href$="/my-projects"]')?.closest('li')?.remove();
        tipRail();
    }

    // The row's identity (name, chips) and its numbers open the dashboard, as
    // the Studio's rows do; buttons inside (Upgrade, Delete) keep their job.
    // One delegated listener on the list, so a redraw never loses it.
    function wireRowClicks() {
        const list = $('formsList');
        if (!list || list.dataset.dv2Clicks) return;
        list.dataset.dv2Clicks = '1';
        list.addEventListener('click', (e) => {
            if (e.target.closest('a, button, select, input, label, .dv2-next')) return;
            const zone = e.target.closest('.dv2-row-head, .dv2-row-meta, .dv2-row-stats');
            const card = zone && zone.closest('.dv2-row[data-form-id]');
            if (!card || !window.myProjects) return;
            const href = window.myProjects._getDashboardLink(card.getAttribute('data-form-id'));
            if (e.metaKey || e.ctrlKey) window.open(href, '_blank'); else window.location.href = href;
        });
    }

    // The account row shows who is signed in, as the home's account menu does,
    // so the rail's footer reads the same on both pages.
    function showAccountEmail() {
        const span = document.querySelector('#dv2AccountBtn span');
        const fb = window.firebase;
        if (!span || !fb || !fb.auth || !fb.apps || !fb.apps.length) return;
        fb.auth().onAuthStateChanged((user) => {
            if (user && user.email) { span.textContent = user.email; span.title = user.email; tipRail(); }
        });
    }

    // ?act=<action>&event=<id> from an event's Settings tab: the event's card,
    // scrolled to and lit, and the card's own handler for the action (the
    // website needs only the card).
    function openRequestedOnHome() {
        const params = new URLSearchParams(window.location.search);
        const act = params.get('act'), ev = params.get('event');
        if (!act || !ev) return;
        ['act', 'event'].forEach((k) => params.delete(k));
        const q = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
        const cardOf = () => document.querySelector(`#formsList .mp-form-card[data-form-id="${CSS.escape(ev)}"]`);
        let tries = 0;
        const go = () => {
            const ready = !!(window.myProjects?.forms || []).some((f) => f.id === ev) && !!cardOf();
            if (!ready) { if (++tries < 60) setTimeout(go, 250); return; }
            actOnEvent(act, ev, cardOf());
        };
        go();
    }

    function actOnEvent(act, id, card) {
        const mp = window.myProjects;
        const f = mp.forms.find((x) => x.id === id);
        const title = f.title || '';
        const lit = card.closest('.dv2-row') || card;
        lit.scrollIntoView({ block: 'center' });
        lit.classList.add('dv2-flash');
        setTimeout(() => lit.classList.remove('dv2-flash'), 2600);
        if (act === 'duplicate') mp.duplicateForm(id);
        else if (act === 'delete') mp.confirmDeleteForm(id, title, !!window.FeatureGates?.isPaidForm?.(f));
        else if (act === 'template') mp.openSaveTemplate(id, title);
        else if (act === 'branding') mp.openPlannerBranding(id, title);
    }

    function initHome() {
        openRequestedOnHome();
        placeRailWidgets();
        wireRowClicks();
        new MutationObserver(placeRailWidgets).observe(document.body, { childList: true, subtree: true });
        let queued = false;
        // setTimeout, not requestAnimationFrame: rAF does not fire in a
        // background tab, so a home opened with ctrl-click stayed unshaped
        // until it was focused.
        const run = () => {
            if (queued) return;
            queued = true;
            setTimeout(() => { queued = false; decorateHome(); }, 0);
        };
        document.addEventListener('mp:forms-loaded', run);
        // The list is redrawn wholesale (counts, pairings, a delete); re-add the
        // row extras each time. decorateHome writes only on change, so its own
        // writes settle instead of looping.
        const list = $('formsList');
        if (list) new MutationObserver(run).observe(list, { childList: true, subtree: true });
    }

    // ---- Event dashboard: the header's controls live in the rail ------------
    // Moved, not copied: the ids stay, so dashboard-quick-actions.js still
    // shows, hides and recolours them; the topbar itself is hidden by CSS.
    // The event's direct actions (guest link, Preview, Edit form, Share access,
    // notifications, Upgrade) sit on the right of the event header on a
    // computer, as a corporate event page's do; on a phone, where the header
    // has no room, they stay in the menu under This event. Re-placed when the
    // window crosses the phone width.
    function placeEventActions() {
        const head = $('dv2HeadActs'), menu = $('dv2EventActions');
        const row = document.querySelector('.dv2-guestlink-row'), links = $('dv2EventLinks');
        if (!head || !menu || !row || !links) return;
        const to = isPhone() ? menu : head;
        if (row.parentElement !== to) to.appendChild(row);
        if (links.parentElement !== to) to.appendChild(links);
    }

    function placeEventControls(id) {
        const move = (elId, slot) => {
            const el = $(elId); const to = $(slot);
            if (el && to && el.parentElement !== to) to.appendChild(el);
        };
        move('header-copy-link-btn', 'dv2GuestLinkActs');
        move('header-download-qr-btn', 'dv2GuestLinkActs');
        move('header-edit-form-btn', 'dv2EventLinks');
        move('header-preview-form-btn', 'dv2EventLinks');
        move('header-share-dashboard-btn', 'dv2EventLinks');
        move('header-response-notif-btn', 'dv2EventLinks');
        move('header-upgrade-btn', 'dv2EventLinks');
        move('header-event-status-btn', 'dv2EventLinks');
        placeEventActions();
        // The guest link is one button: copying is what hosts do with it (the
        // QR code and a guest's view of the form are in Settings). It presses
        // the copy button, which hands out the form's public link and says so.
        const link = $('dv2GuestLink');
        // Edit is the guest link's partner in the header, labelled the same way
        // (its label is its title, drawn by CSS; the phone menu shows it too).
        const editBtn = $('header-edit-form-btn');
        if (editBtn) editBtn.title = t('editForm');
        if (link && id) {
            link.title = window.location.host + '/' + id;
            $('dv2GuestLinkText').textContent = t('guestLink');
            if (!link.dataset.wired) {
                link.dataset.wired = '1';
                link.addEventListener('click', () => $('header-copy-link-btn')?.click());
            }
        }
    }

    // Short labels on the summary tiles ("Confirmed Guests" truncated at seven
    // tiles), a tooltip on the now icon-only Export CSV, and the "Party of"
    // prefix the phone check-in cards draw in CSS. localizeDashboardChrome has
    // already run by now.
    function calmChrome() {
        const labels = { 'total-count': 'sReplies', 'total-guests': 'sGuests', 'adults-count': 'sAdults', 'children-count': 'sChildren', 'maybe-count': 'sMaybe', 'awaiting-count': 'sAwaiting', 'waitlisted-count': 'sWaitlisted' };
        Object.entries(labels).forEach(([valueId, key]) => {
            const label = $(valueId)?.parentElement?.querySelector('.stat-label');
            if (label) label.textContent = t(key);
        });
        const exp = $('export-csv-btn');
        if (exp) { exp.title = t('exportCsv'); exp.setAttribute('aria-label', t('exportCsv')); }
        document.documentElement.style.setProperty('--dv2-party-of', JSON.stringify(t('partyOf')));
    }

    // Parts of the event: each pill carries its fill as --pct so the tile can
    // draw a capacity bar ("36 / 200" -> 18%). admin.js redraws the pills on
    // every refresh, so this follows the box.
    function initPartBars() {
        const box = $('event-parts-stats');
        if (!box) return;
        const paint = () => {
            box.querySelectorAll('.part-filter-pill').forEach((pill) => {
                const m = (pill.querySelector('b')?.textContent || '').match(/(\d+)\s*\/\s*(\d+)/);
                const pct = m && Number(m[2]) > 0 ? Math.min(100, Math.round((Number(m[1]) / Number(m[2])) * 100)) : null;
                const v = pct == null ? '' : pct + '%';
                if (pill.style.getPropertyValue('--pct') !== v) {
                    if (v) pill.style.setProperty('--pct', v); else pill.style.removeProperty('--pct');
                }
                pill.classList.toggle('has-cap', pct != null);
                const name = pill.querySelector('bdi');
                if (name && name.title !== name.textContent) name.title = name.textContent;
            });
        };
        new MutationObserver(paint).observe(box, { childList: true, subtree: true, characterData: true });
        paint();
    }

    // ---- Event dashboard: next step, last-seen, address, phone "More" -------
    // Task #998 (Esther): on a phone the Responses toolbar is two rows - Add
    // Guest + status, then search + language - and one "..." button that
    // shows the rest (sort, Import, Add invitees, Export) underneath. Only a
    // class on the toolbar; the controls stay where admin.js and the import
    // wizard put them, with their own handlers. Above 767px the button is
    // hidden and the toolbar is as before.
    function initResponsesToolbarMore() {
        const bar = document.querySelector('#responses .responses-toolbar');
        if (!bar || bar.querySelector('.toolbar-more')) return;
        const wrap = document.createElement('div');
        wrap.className = 'toolbar-group toolbar-more';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm btn-outline-secondary';
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-label', t('moreTools'));
        btn.title = t('moreTools');
        btn.innerHTML = '<i class="bi bi-three-dots" aria-hidden="true"></i>';
        btn.addEventListener('click', () => {
            const open = bar.classList.toggle('is-more-open');
            btn.setAttribute('aria-expanded', String(open));
        });
        wrap.appendChild(btn);
        bar.appendChild(wrap);
        // The status filter is behind "..." too (round 2): a dot on it says
        // the list is filtered while the row is closed. The held-replies
        // banner sets the filter in code and re-renders the list, so the
        // re-render is watched as well as the change.
        const filter = $('attendance-filter');
        const sync = () => btn.classList.toggle('has-filter', !!filter && filter.value !== 'all');
        filter?.addEventListener('change', sync);
        const list = $('responses-container');
        if (list && typeof MutationObserver === 'function') new MutationObserver(sync).observe(list, { childList: true });
        sync();
    }

    function initEvent() {
        const id = currentFormId();
        placeEventControls(id);
        calmChrome();
        initPartBars();
        initResponsesToolbarMore();
        // Messaging's action buttons share a row on phones (CSS keys on this).
        $('campaignsAllContactsBtn')?.parentElement?.classList.add('dv2-msg-actions');

        // The open section rides in the address, so a refresh or a shared
        // link lands on it, and Clarity draws each tab as its own page.
        // Responses carries ?tab=responses too (set on load in the page head).
        document.querySelectorAll('#dashboardTabs [data-bs-toggle="tab"]').forEach((btn) => {
            btn.addEventListener('shown.bs.tab', () => {
                try {
                    const u = new URL(window.location.href);
                    const tab = (btn.dataset.bsTarget || '#responses').slice(1);
                    u.searchParams.set('tab', tab);
                    history.replaceState(history.state, '', u.pathname + u.search + u.hash);
                } catch (e) { /* leave the address as it is */ }
            });
        });

        // Next step and last-seen follow the summary strip admin.js keeps
        // current. Under a filter the strip reads "12 / 51"; the total is the
        // last number.
        const totalEl = $('total-count');
        const lastNumber = (el) => { const m = (el?.textContent || '').match(/\d+/g); return m ? Number(m[m.length - 1]) : NaN; };
        // The first pass reads the page's placeholder "0", not the event: it
        // must not be remembered (it made every reply "new" next time). Every
        // later pass is admin.js writing the real count.
        const refresh = (loaded = true) => {
            const replies = lastNumber(totalEl);
            if (Number.isNaN(replies)) return;
            const fd = window.currentFormData || {};
            const awaitingBox = $('awaiting-stat-container');
            const awaiting = awaitingBox && awaitingBox.style.display !== 'none' ? (lastNumber($('awaiting-count')) || 0) : 0;
            if (id && loaded && !window.viewerMode) rememberSeen(id, replies);
            // Nor may it advise: the placeholder read as "No replies yet — send
            // invitations" under the loading spinner on events full of replies.
            if (loaded) renderNextStep(nextStep({ replies, awaiting, eventDate: fd.eventDate, rsvpDeadline: fd.rsvpDeadline, messaged: !!fd.firstMessageSentAt }));
            if ($('settings')?.classList.contains('active')) renderSettings();
            // Date and venue under the event's name, as the corporate
            // dashboard shows them under its event title.
            const sub = $('dv2EventSub');
            if (sub) {
                const venue = String(fd.eventLocation || fd.location || fd.venue || '').trim();
                const bits = [formatDate(fd.eventDate), venue].filter(Boolean);
                setHtml(sub, esc(bits.join(' · ')));
                sub.title = bits.join(' · ');
            }
            renderPlanLine();
        };
        // The Upgrade button's visibility is settled by the form read in
        // dashboard.html, and the wall's numbers by response-limits.js; both
        // can land after the count, so the plan line follows each.
        const upgradeBtn = $('header-upgrade-btn');
        if (upgradeBtn) new MutationObserver(() => renderPlanLine()).observe(upgradeBtn, { attributes: true, attributeFilter: ['style'] });
        document.addEventListener('qr:reply-wall', () => renderPlanLine());
        if (totalEl) {
            new MutationObserver(() => refresh()).observe(totalEl, { childList: true, characterData: true, subtree: true });
            refresh(false);
        }
        watchEmptyStats();
    }

    // A new event is a wall of zeros: seven tiles at 0 and "No dietary data".
    // Every tile but Replies hides while it reads 0 (dv2-zero), and the
    // dietary row while it has nothing but admin.js's empty note (dv2-empty).
    // admin.js rewrites the values in place, so this watches them.
    function watchEmptyStats() {
        const tiles = document.querySelector('.stats-bar-items');
        const diet = $('dietary-stats'), dietPanel = $('dietary-stats-panel');
        const lastNumber = (el) => { const m = (el?.textContent || '').match(/\d+/g); return m ? Number(m[m.length - 1]) : NaN; };
        const sync = () => {
            tiles?.querySelectorAll('.stat-inline:not(.stat-primary)').forEach((tile) => {
                tile.classList.toggle('dv2-zero', lastNumber(tile.querySelector('.stat-value')) === 0);
            });
            if (diet && dietPanel) dietPanel.classList.toggle('dv2-empty', !diet.querySelector(':scope > :not(.text-muted)'));
        };
        [tiles, diet].forEach((el) => { if (el) new MutationObserver(sync).observe(el, { childList: true, characterData: true, subtree: true }); });
        sync();
    }

    // ---- Settings: what the event's My Projects card held -----------------------
    // One list of the event's own controls. A control the page already has is
    // that same button, clicked through (data-click), so there is one
    // implementation of each. Duplicate, template, branding, the website and
    // delete live in My Projects: those rows open it on this event (data-act,
    // run by openRequestedOnHome), where the card's own handlers do the work.
    const shown = (el) => !!el && el.style.display !== 'none' && !el.hidden;
    function settingsRow(icon, label, hint, attrs, danger) {
        return `<button type="button" class="dv2-set-row${danger ? ' is-danger' : ''}" ${attrs}><i class="bi ${icon}"></i>`
            + `<span class="dv2-set-text"><b>${esc(label)}</b>${hint ? `<small>${esc(hint)}</small>` : ''}</span><i class="bi bi-chevron-right dv2-set-go"></i></button>`;
    }
    const settingsGroup = (title, rows) => (rows.filter(Boolean).length
        ? `<section class="dv2-set-group"><h2 class="dv2-set-head">${esc(title)}</h2><div class="dv2-set-list">${rows.join('')}</div></section>`
        : '');

    function renderSettings() {
        const box = $('dv2Settings');
        if (!box || !currentFormId()) return;
        const fd = window.currentFormData || {};
        const user = window.firebase && window.firebase.auth && window.firebase.apps && window.firebase.apps.length ? window.firebase.auth().currentUser : null;
        // A demo has no account to hand off to; a co-host may not copy or delete.
        const handOff = !window.isDemoMode && !!user;
        const owner = handOff && (!fd.owner || fd.owner === user.uid);
        const planner = window.PlannerSubscription?.isActive?.() === true;
        const paid = !!window.FeatureGates?.isPaidForm?.(fd);
        const click = (id, icon, label, hint) => (shown($(id)) ? settingsRow(icon, label, hint, `data-click="${id}"`) : '');
        // The bell is lit (btn-warning) while email notifications are on.
        const notifRow = () => {
            const bell = $('header-response-notif-btn');
            if (!shown(bell)) return '';
            return settingsRow('bi-bell', t('setNotif'), t('setNotifHint'), 'data-click="header-response-notif-btn"')
                .replace('</b>', bell.classList.contains('btn-warning') ? `<span class="dv2-set-on">${esc(t('on'))}</span></b>` : '</b>');
        };
        const act = (name, icon, label, hint, danger) => settingsRow(icon, label, hint, `data-act="${name}"`, danger);
        let html = settingsGroup(t('setSharing'), [
            click('header-share-dashboard-btn', 'bi-person-plus', t('setAccess'), t('setAccessHint')),
            notifRow(),
            click('header-download-qr-btn', 'bi-qr-code', t('setQr'), ''),
        ]);
        html += settingsGroup(t('setForm'), [
            click('header-edit-form-btn', 'bi-pencil-square', t('setEdit'), t('setEditHint')),
            // The real form beside its preview. Copying the link was a row here
            // too, though "Copy guest link" never leaves the header (or the
            // phone menu); opening it is what Settings lacked (2026-10-25).
            shown($('header-copy-link-btn')) ? settingsRow('bi-box-arrow-up-right', t('setOpenForm'), t('setOpenFormHint'), 'data-open-form="1"') : '',
            // With tagged parts the preview is the only place a host can see a
            // tagged guest's form (the real link shows a stranger's), so say so.
            click('header-preview-form-btn', 'bi-eye', t('setPreview'),
                t(window.EventParts?.hasTaggedParts?.(fd.eventParts) ? 'setPreviewTagsHint' : 'setPreviewHint')),
            // Postpone / new date / cancel (task #955): its words come from its own window.
            click('header-event-status-btn', 'bi-calendar2-x', (window.EventStatusControl && window.EventStatusControl.label('button')) || 'Postpone or cancel', ''),
        ]);
        html += settingsGroup(t('setPlan'), [click('header-upgrade-btn', 'bi-arrow-up-circle', t('setUpgrade'), t('setUpgradeHint'))]);
        html += settingsGroup(t('setSiteReg'), [
            shown($('website-tab-nav')) ? settingsRow('bi-globe2', t('setWebsite'), '', 'data-click="website-tab"') : '',
            shown($('registry-tab-nav')) ? settingsRow('bi-gift', t('setRegistry'), t('setRegistryHint'), 'data-click="registry-tab"') : '',
        ]);
        if (owner) {
            html += settingsGroup(t('setCopies'), [
                act('duplicate', 'bi-files', t('setDuplicate'), t('setDuplicateHint')),
                planner && paid ? act('template', 'bi-bookmark-plus', t('setTemplate'), '') : '',
                planner ? act('branding', 'bi-palette', t('setBranding'), '') : '',
            ]);
            html += settingsGroup('', [act('delete', 'bi-trash', t('setDelete'), t('setDeleteHint'), true)]);
        } else if (handOff) {
            html += `<p class="dv2-set-note">${esc(t('setShared'))}</p>`;
        }
        setHtml(box, html);
    }

    // ---- Website (task #984) -----------------------------------------------
    // The event's wedding website inside the dashboard, as the Registry tab
    // shows its registry (round 2, Esther: a tab that left the dashboard at
    // once was not wanted): the site at a glance - status, address, a live
    // preview - a shortcut into each section of the builder (its ?focus=
    // deep links, task #988), and the builder for everything else. The
    // event's site is the one whose RSVP page points at this form, as My
    // Projects pairs them, else the one whose Event Pro credit was claimed
    // for it; with none, the owner is offered a new site attached to this
    // event. Signed-in hosts only: a demo or a shared view has no websites.
    function websiteBuilderBase() {
        return (PREFIX || '') + '/wedding-websites/builder';
    }
    function formIdOfSite(site) {
        const url = site && site.pages && site.pages.rsvp && site.pages.rsvp.formUrl;
        if (url) {
            try {
                const u = new URL(String(url), window.location.origin);
                const id = u.searchParams.get('formId') || u.searchParams.get('id') || (u.pathname.split('/').filter(Boolean).pop() || '');
                if (id && !/\.html?$/i.test(id)) return id;
            } catch (e) {
                const bare = String(url).trim();
                if (/^[A-Za-z0-9_-]{6,}$/.test(bare)) return bare;
            }
        }
        return (site && site.linkedEventProFormId) || null;
    }
    const websiteUser = () => (window.firebase && window.firebase.apps && window.firebase.apps.length ? window.firebase.auth().currentUser : null);
    // The builder's sections, by its ?focus= names (wedding-builder.js WB_FOCUS).
    const WEBSITE_SECTIONS = [
        ['names-date', 'bi-people', 'webNames'], ['template', 'bi-palette', 'webDesign'],
        ['page-home', 'bi-house', 'webHome'], ['page-details', 'bi-calendar-event', 'webDetails'],
        ['page-travel', 'bi-geo-alt', 'webTravel'], ['page-faq', 'bi-question-circle', 'webFaq'],
        ['page-photos', 'bi-images', 'webPhotos'], ['page-rsvp', 'bi-clipboard-check', 'webRsvp'],
        ['site-link', 'bi-link-45deg', 'webAddress'], ['languages', 'bi-translate', 'webLangs']
    ];
    function websiteState(site) {
        const expires = site.expiresAt && site.expiresAt._seconds ? new Date(site.expiresAt._seconds * 1000) : null;
        if (site.isPaid === true && expires && expires < new Date()) return 'expired';
        return site.status === 'published' || (site.settings && site.settings.isPublished === true) ? 'live' : 'draft';
    }
    function websiteHtml(site) {
        const edit = `${websiteBuilderBase()}?id=${encodeURIComponent(site.id)}`;
        const names = [site.partner1Name, site.partner2Name].filter(Boolean).join(' & ') || site.coupleNames || t('webWord');
        const state = websiteState(site);
        const url = `${window.location.origin}/w/${site.subdomain || ''}`;
        const date = site.weddingDate && site.weddingDate._seconds
            ? new Date(site.weddingDate._seconds * 1000).toLocaleDateString(LANG, { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        const badge = state === 'live' ? `<span class="badge bg-success">${esc(t('webLive'))}</span>`
            : state === 'expired' ? `<span class="badge bg-danger">${esc(t('webExpired'))}</span>`
                : `<span class="badge bg-secondary">${esc(t('webDraft'))}</span>`;
        const address = state === 'live'
            ? `<div class="dv2-web-address"><a href="${esc(url)}" target="_blank" rel="noopener" dir="ltr"><i class="bi bi-link-45deg"></i>${esc(url.replace(/^https?:\/\//, ''))}</a>`
              + `<span class="dv2-web-acts"><button type="button" class="btn btn-sm btn-outline-secondary" data-web-copy="${esc(url)}"><i class="bi bi-copy me-1"></i>${esc(t('webCopy'))}</button>`
              + `<a class="btn btn-sm btn-outline-secondary" href="${esc(url)}" target="_blank" rel="noopener"><i class="bi bi-box-arrow-up-right me-1"></i>${esc(t('webView'))}</a></span></div>`
            : `<div class="dv2-web-note"><span>${esc(t(state === 'expired' ? 'webExpiredNote' : 'webDraftNote'))}</span>`
              + `<a class="btn btn-sm btn-primary" href="${esc(edit)}&focus=publish">${esc(t(state === 'expired' ? 'webRenew' : 'webPublish'))}</a></div>`;
        const rows = WEBSITE_SECTIONS.map(([focus, icon, key]) => settingsRow(icon, t(key), '', `data-web-go="${esc(edit)}&focus=${focus}"`));
        // The live site itself, as guests see it. A draft has no public page
        // to show: its preview is the builder's.
        const preview = state === 'live'
            ? `<div class="dv2-web-preview"><h2 class="dv2-set-head">${esc(t('webPreview'))}</h2><iframe src="${esc(url)}" title="${esc(t('webPreview'))}" loading="lazy" tabindex="-1"></iframe></div>` : '';
        return `<div class="dv2-web">
            <div class="dv2-web-top">
                <div class="dv2-web-title"><h5 class="mb-0"><i class="bi bi-globe2 text-primary me-2"></i>${esc(names)}</h5>${badge}${date ? `<span class="dv2-web-date">${esc(date)}</span>` : ''}</div>
                <a class="btn btn-sm btn-primary" href="${esc(edit)}"><i class="bi bi-pencil-square me-1"></i>${esc(t('webEdit'))}</a>
            </div>
            ${address}
            <div class="dv2-web-body${preview ? ' has-preview' : ''}">
                <div class="dv2-web-sections">${settingsGroup(t('webSections'), rows)}</div>
                ${preview}
            </div>
        </div>`;
    }
    function websiteNudgeHtml(owner) {
        if (!owner) return `<p class="dv2-set-note">${esc(t('webOwnerOnly'))}</p>`;
        return `<div class="dv2-web-nudge"><i class="bi bi-globe2"></i><h5>${esc(t('webNudgeTitle'))}</h5><p>${esc(t('webNudgeDesc'))}</p>`
            + `<a class="btn btn-primary" href="${esc(websiteBuilderBase())}?formId=${encodeURIComponent(currentFormId())}"><i class="bi bi-plus-lg me-1"></i>${esc(t('webCreate'))}</a></div>`;
    }
    let websiteSeq = 0;
    async function renderWebsite() {
        const box = $('dv2Website');
        const id = currentFormId();
        const u = websiteUser();
        if (!box || !id || !u) return;
        const seq = ++websiteSeq;
        if (box.dataset.formId !== id) {
            box.dataset.formId = id;
            setHtml(box, '<div class="text-center py-5"><div class="spinner-border text-secondary" role="status"></div></div>');
        }
        let html;
        try {
            const token = await u.getIdToken();
            const res = await fetch('/api/wedding-websites', { headers: { Authorization: 'Bearer ' + token } });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const data = await res.json();
            // Two sites can point at one event (the Website button autosaves a
            // blank draft). The list is newest first, so the blank one would
            // win; the site with the couple's names on it is the real one.
            const mine = (data.websites || []).filter((s) => formIdOfSite(s) === id);
            const site = mine.find((s) => s.partner1Name || s.partner2Name) || mine[0] || null;
            const fd = window.currentFormData || {};
            html = site ? websiteHtml(site) : websiteNudgeHtml(!fd.owner || fd.owner === u.uid);
        } catch (e) {
            html = `<p class="dv2-set-note">${esc(t('webLoadError'))}</p>`;
        }
        if (seq === websiteSeq && currentFormId() === id) setHtml(box, html);
    }
    function initWebsiteTab() {
        const nav = $('website-tab-nav');
        const box = $('dv2Website');
        if (!nav || !box) return;
        const sync = () => { nav.hidden = !!window.isDemoMode || !!window.viewerMode || !websiteUser() || !currentFormId(); };
        sync();
        try { window.firebase.auth().onAuthStateChanged(sync); } catch (e) { /* no auth on this page */ }
        $('website-tab')?.addEventListener('show.bs.tab', renderWebsite);
        box.addEventListener('click', (e) => {
            const go = e.target.closest('[data-web-go]');
            if (go) { window.location.href = go.dataset.webGo; return; }
            const copy = e.target.closest('[data-web-copy]');
            if (!copy) return;
            const done = () => {
                const label = copy.innerHTML;
                copy.innerHTML = `<i class="bi bi-check2 me-1"></i>${esc(t('webCopied'))}`;
                setTimeout(() => { copy.innerHTML = label; }, 1800);
            };
            navigator.clipboard?.writeText(copy.dataset.webCopy).then(done, () => {});
        });
    }

    function initSettings() {
        const box = $('dv2Settings');
        if (!box) return;
        $('settings-tab')?.addEventListener('show.bs.tab', renderSettings);
        const bell = $('header-response-notif-btn');
        if (bell) new MutationObserver(() => { if ($('settings')?.classList.contains('active')) renderSettings(); })
            .observe(bell, { attributes: true, attributeFilter: ['class', 'style'] });
        box.addEventListener('click', (e) => {
            const row = e.target.closest('.dv2-set-row');
            const id = currentFormId();
            if (!row || !id) return;
            if (row.dataset.click) { $(row.dataset.click)?.click(); return; }
            // The same address "Copy guest link" hands out.
            if (row.dataset.openForm) { window.open(`${window.location.origin}/${encodeURIComponent(id)}`, '_blank', 'noopener'); return; }
            if (row.dataset.act) window.location.href = `${PREFIX}/dashboard?all=1&act=${row.dataset.act}&event=${encodeURIComponent(id)}`;
        });
        if (new URLSearchParams(window.location.search).get('tab') === 'settings') {
            let tries = 0;
            const go = () => {
                const c = $('dashboard-container');
                if (c && c.style.display !== 'none' && window.bootstrap?.Tab) window.bootstrap.Tab.getOrCreateInstance($('settings-tab')).show();
                else if (++tries < 40) setTimeout(go, 250);
            };
            go();
        }
    }

    // ---- The Account window: language and sign out ------------------------------
    // The rail had a language menu on both pages; it is a set-once choice, so it
    // moved into the Account window; neither page has the old menu.
    // Changing it keeps the host on the same page. Sign out sits by the
    // email: on an event page this window is the only account menu.
    const LANG_NAMES = { en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português', he: 'עברית', ar: 'العربية' };
    function goToLanguage(lang) {
        if (!LANG_NAMES[lang] || lang === LANG) return;
        try { window.LanguageManager?.setUserPreference?.(lang); } catch (err) { /* best effort */ }
        try { localStorage.setItem('userLanguagePreference', lang); } catch (err) { /* private mode */ }
        window.location.href = (lang === 'en' ? '' : '/' + lang) + '/dashboard' + window.location.search + window.location.hash;
    }
    function signOut() {
        // This tab's copy of the event list goes with the account, and so
        // does "has seen an event" (the next account arrives afresh).
        try { sessionStorage.removeItem(EVENTS_KEY); sessionStorage.removeItem(SEEN_EVENT_KEY); } catch (err) { /* private mode */ }
        const leave = () => { window.location.href = PREFIX + '/'; };
        const auth = window.firebaseAuth || (window.firebase && window.firebase.auth && window.firebase.auth());
        if (!auth) { leave(); return; }
        auth.signOut().then(leave, leave);
    }
    // Signing out by any route (Sign out here, the home's account menu, another
    // tab) must leave nothing of the account on screen. My Projects redraws
    // its own part signed out, but not what v2 added: the totals strip, the
    // credits and planner lines, the sidebar's events. So a page that loses
    // its signed-in host leaves, as the event page does.
    function watchSignOut() {
        const fb = window.firebase;
        if (!fb || !fb.auth || !fb.apps || !fb.apps.length || window.isDemoMode) return;
        let hadHost = false;
        fb.auth().onAuthStateChanged((user) => {
            if (user && !user.isAnonymous) { hadHost = true; return; }
            if (!hadHost) return;
            try { sessionStorage.removeItem(EVENTS_KEY); } catch (err) { /* private mode */ }
            window.location.replace(PREFIX + '/');
        });
    }
    // The address book opens where the host is. My Projects loads
    // address-book.js; the event page loads it on first use, as the
    // campaign wizard does. (It used to send the event page to All events.)
    let addressBookLoading = null;
    async function openAddressBook() {
        if (!window.AddressBook) {
            addressBookLoading = addressBookLoading || new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = '/assets/js/address-book.js';
                s.onload = resolve;
                s.onerror = reject;
                document.head.appendChild(s);
            });
            try { await addressBookLoading; } catch (e) { addressBookLoading = null; return; }
        }
        window.AddressBook?.open();
    }
    function initAddressBook() {
        document.addEventListener('click', (e) => {
            if (!e.target.closest('[data-dv2-address-book]')) return;
            setSheet(false);
            openAddressBook();
        });
    }
    // The rail's Language line (task #929): the same move as the Account
    // window's picker.
    function initRailLanguage() {
        document.querySelectorAll('select[data-dv2-lang]').forEach((sel) => {
            sel.value = LANG;
            sel.addEventListener('change', () => goToLanguage(sel.value));
        });
    }
    function initAccountWindow() {
        document.addEventListener('show.bs.modal', (e) => {
            const modal = e.target;
            if (!modal || modal.id !== 'accountModal' || modal.querySelector('.dv2-acct-lang')) return;
            const info = modal.querySelector('#accountModalUserInfo');
            if (!info) return;
            const out = document.createElement('button');
            out.type = 'button';
            out.className = 'btn btn-outline-secondary btn-sm dv2-acct-signout';
            out.innerHTML = `<i class="bi bi-box-arrow-right"></i>${esc(t('signOut'))}`;
            out.addEventListener('click', signOut);
            info.querySelector('.d-flex')?.appendChild(out);
            const row = document.createElement('div');
            row.className = 'dv2-acct-lang';
            row.innerHTML = `<label for="dv2AcctLang"><i class="bi bi-globe2"></i>${esc(t('language'))}</label>`
                + `<select class="form-select form-select-sm" id="dv2AcctLang">${Object.entries(LANG_NAMES).map(([code, name]) => `<option value="${code}"${code === LANG ? ' selected' : ''}>${name}</option>`).join('')}</select>`;
            row.querySelector('select').addEventListener('change', (ev) => goToLanguage(ev.target.value));
            info.insertAdjacentElement('afterend', row);
        });
    }

    // ---- Wiring ------------------------------------------------------------
    function init() {
        if (!body.classList.contains('dv2')) return;

        const isHome = body.classList.contains('dv2-home');
        if (!isHome && currentFormId()) {
            try { sessionStorage.setItem(SEEN_EVENT_KEY, '1'); } catch (e) { /* private mode */ }
        }
        initAccountWindow();
        initRailLanguage();
        initAddressBook();
        watchSignOut();
        if (!body.classList.contains('dv2-home')) showAccountEmail();
        if (isHome) initHome(); else initEvent();

        // dv2-collapsed (the desktop choice, remembered) and dv2-expanded (a
        // tablet rail opened for now) are state; dv2-narrow is what CSS reads.
        const syncNarrow = () => {
            body.classList.toggle('dv2-narrow', !isPhone()
                && (isTablet() ? !body.classList.contains('dv2-expanded') : body.classList.contains('dv2-collapsed')));
            tipRail();
        };
        try { if (localStorage.getItem(COLLAPSE_KEY) === '1') body.classList.add('dv2-collapsed'); } catch (e) { /* private mode */ }
        syncNarrow();
        window.matchMedia('(max-width: 767px)').addEventListener?.('change', syncNarrow);
        window.matchMedia('(min-width: 768px) and (max-width: 1100px)').addEventListener?.('change', syncNarrow);

        $('dv2CollapseBtn')?.addEventListener('click', () => {
            if (isTablet()) { body.classList.toggle('dv2-expanded'); syncNarrow(); return; }
            const on = body.classList.toggle('dv2-collapsed');
            try { localStorage.setItem(COLLAPSE_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
            syncNarrow();
        });

        $('dv2NewBtn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            setNewMenu($('dv2NewMenu').hidden);
        });
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.dv2-create')) setNewMenu(false);
        });
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            setNewMenu(false);
            setSheet(false);
        });
        initEventList();
        initSettings();
        initWebsiteTab();

        $('dv2MenuBtn')?.addEventListener('click', () => setSheet(!body.classList.contains('dv2-sheet-open')));
        $('dv2TabsMenuBtn')?.addEventListener('click', () => setSheet(!body.classList.contains('dv2-sheet-open')));
        window.matchMedia('(max-width: 767px)').addEventListener?.('change', placeEventActions);
        $('dv2Scrim')?.addEventListener('click', () => setSheet(false));
        document.querySelectorAll('#dashboardTabs [data-bs-toggle="tab"]').forEach((btn) => {
            btn.addEventListener('shown.bs.tab', () => {
                setSheet(false);
                window.scrollTo({ top: 0 });
                showActiveSection();
            });
        });
        showActiveSection();
        initBarOverflow();
        watchTables();

        const title = $('dashboard-title');
        if (title) {
            new MutationObserver(syncEventName).observe(title, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['title'] });
            syncEventName();
        }
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
